import React from 'react';
import UnifiedSimpleConversionExplanation from './UnifiedSimpleConversionExplanation';

const TemperatureConversionExplanation = ({
  fromUnit,
  toUnit,
  inputValue,
  result,
  darkMode
}) => {
  const getConversionData = () => {
    // Set a proper example value when no result exists
    const exampleValue = "25"; // Our default example
    const valueToUse = result ? inputValue : exampleValue;
    
    // Inside getConversionData for any conversion type
    const formatConversionSteps = (value, fromUnit, formula, calculation, result) => {
      // Parse the value to ensure we're working with a number
      const numericValue = parseFloat(value);

      // Create the highlighted value string
      // Create a highlighted value without unit for formula substitution
      const highlightedValueNoUnit = `<span class="text-blue-600 dark:text-blue-400">${numericValue}</span>`;

      // Replace the input unit (e.g., °C, K, °F) with the highlighted value (without unit)
      const formulaWithValue = formula.replace(
        new RegExp(`(${fromUnit}|°[CFKNDR][ée]?)(?![^=]*=)`, 'g'), 
        highlightedValueNoUnit
      );

      return [
        // Step 0: Just the value (for blue highlighting)
        `${numericValue}${fromUnit}`,
        
        // Step 1: Formula with substituted value (in correct position)
        formulaWithValue,
        
        // Step 2: Complete calculation
        calculation,
        
        // Step 3: Final result
        `Final result: ${result}`
      ];
    };

    // Celsius to Fahrenheit
    if (fromUnit === 'Celsius' && toUnit === 'Fahrenheit') {
      const step1 = parseFloat(valueToUse) * (9/5);
      const step2 = step1 + 32;
      const finalResult = result ? result : step2.toFixed(2);

      return {
        introduction: [
          "The Celsius and Fahrenheit scales were developed independently, using different reference points and different interval sizes.",
          
          "Anders Celsius chose the freezing and boiling points of water as his reference points, assigning 0° to freezing and 100° to boiling. This created a logical scale based on water's phase changes.",
          
          "Daniel Fahrenheit, on the other hand, used a mixture of ice, water, and ammonium chloride for his zero point, and approximated normal human body temperature as 96°F. This led to water freezing at 32°F and boiling at 212°F.",
        ],
        fundamentalEquality: {
          statement: `0°C = 32°F  and  100°C = 212°F`,
          explanation: `These relationships show us how the scales align at water's freezing and boiling points`
        },
        formula: {
          main: `°F = (°C × 9/5) + 32`,
          alternate: `°F = (°C × 1.8) + 32`
        },
        derivationSteps: [
          {
            step: "Understand the interval relationship",
            explanation: "The Fahrenheit scale divides the same temperature range into 180 parts (212°F - 32°F), while Celsius uses 100 parts (100°C - 0°C)"
          },
          {
            step: "Calculate the conversion factor",
            explanation: "This gives us the ratio 180/100 = 9/5 (or 1.8) as our multiplication factor"
          },
          {
            step: "Account for different zero points",
            explanation: "Since 0°C equals 32°F, we add 32 to complete the conversion"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°C',                                     // fromUnit
          '°F = (°C × 9/5) + 32',                  // formula
          `°F = (${valueToUse} × 9/5) + 32 = ${finalResult}°F`,  // calculation with actual values
          `${finalResult}°F`                         // result
        ),
        inputValue: valueToUse,  // Make sure we pass this through
      };
    }

    // Celsius to Kelvin
    if (fromUnit === 'Celsius' && toUnit === 'Kelvin') {
      const step1 = parseFloat(valueToUse) + 273.15;
      const finalResult = result || (step1.toFixed(2));

      return {
        introduction: [
          "The Celsius to Kelvin conversion is fundamental in scientific work, representing the bridge between everyday temperature measurements and the absolute temperature scale used in scientific calculations.",
          
          "While Anders Celsius based his scale on water's phase changes, William Thomson (Lord Kelvin) sought to create a scale starting at the lowest possible temperature in the universe - absolute zero. This point, where molecular motion theoretically stops, was determined to be -273.15°C.",
          
          "The relationship between these scales is uniquely elegant: they share the same size degree, meaning a one-degree change in Celsius equals a one-degree change in Kelvin. This makes conversion between the two scales particularly straightforward."
        ],
        fundamentalEquality: {
          statement: "-273.15°C = 0K  and  0°C = 273.15K",
          explanation: "These relationships show how absolute zero defines the Kelvin scale's starting point, while maintaining the same degree size as Celsius"
        },
        formula: {
          main: 'K = °C + 273.15',
          alternate: '°C = K - 273.15'
        },
        derivationSteps: [
          {
            step: "Recognize the degree size relationship",
            explanation: "Both scales use the same size degree, so no multiplication factor is needed for conversion"
          },
          {
            step: "Identify the offset value",
            explanation: "The difference between the scales is exactly 273.15 degrees, representing the position of absolute zero on the Celsius scale"
          },
          {
            step: "Apply the offset",
            explanation: "To convert from Celsius to Kelvin, simply add 273.15 to shift the reference point from water's freezing point to absolute zero"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                    // value
          '°C',                         // fromUnit
          'K = °C + 273.15',           // formula
          `K = ${valueToUse} + 273.15 = ${finalResult}K`,  // calculation with actual values
          `${finalResult}K`                     // result
        ),
        realWorldExample: "In thermodynamics calculations, such as those involving gas laws, temperatures must be in Kelvin. For instance, when studying a gas at room temperature (20°C), scientists would convert this to 293.15K for their calculations, as absolute temperature is essential for accurate results.",
        funFacts: [
          "The Kelvin scale is the only temperature scale that doesn't use degrees (°) - temperatures are simply expressed as 'K'.",
          "Water's triple point (where solid, liquid, and vapor coexist) is defined as exactly 273.16K (0.01°C).",
          "The cosmic microwave background radiation, considered the temperature of space, is approximately 2.7K (-270.45°C)."
        ],
        inputValue: valueToUse
      };
    }

    // Fahrenheit to Celsius
    if (fromUnit === 'Fahrenheit' && toUnit === 'Celsius') {
      const step1 = valueToUse - 32;
      const step2 = step1 * (5/9);
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "The Fahrenheit to Celsius conversion is commonly needed when converting between US temperature measurements and those used in most other countries.",
          
          `While Fahrenheit uses 32\u00B0F as the freezing point of water and 212\u00B0F as its boiling point, Celsius uses 0\u00B0C and 100\u00B0C respectively. This means we need to account for both the different zero points and the different interval sizes.`,
          
          "To understand this conversion, let's first look at how these scales align at their key reference points:"
        ],
        fundamentalEquality: {
          statement: `32\u00B0F = 0\u00B0C  and  212\u00B0F = 100\u00B0C`,
          explanation: "These key points show us how the scales align at water's freezing and boiling temperatures"
        },
        formula: {
          main: `\u00B0C = (\u00B0F - 32) × 5/9`,
          alternate: `\u00B0C = (\u00B0F - 32) × 0.5556`
        },
        derivationSteps: [
          {
            step: "Align zero points",
            explanation: "First subtract 32°F to align with the Celsius scale's zero point at water's freezing temperature"
          },
          {
            step: "Adjust for scale intervals",
            explanation: "The Fahrenheit scale divides the range into 180° (212°F - 32°F), while Celsius uses 100°, giving us the ratio 5/9"
          },
          {
            step: "Combine the steps",
            explanation: "This gives us our formula: subtract 32 first, then multiply by 5/9 to convert to Celsius"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°F',                                     // fromUnit
          '\u00B0C = (\u00B0F - 32) × 5/9',                  // formula
          `\u00B0C = (${valueToUse} - 32) × 5/9 = ${finalResult}°C`,  // calculation with actual values
          `${finalResult}°C`                                   // result
        ),
        realWorldExample: "When cooking with a recipe from Europe, you might need to convert your oven's Fahrenheit temperature to Celsius. For instance, a common baking temperature of 350°F converts to about 177°C.",
        funFacts: [
          "The Fahrenheit scale was the primary temperature scale in most English-speaking countries until the 1960s-70s.",
          "Normal human body temperature is about 98.6°F (37°C).",
          "The point where Fahrenheit and Celsius readings are equal is at -40 degrees."
        ],
        inputValue: valueToUse
      };
    }

    // Fahrenheit to Kelvin
    if (fromUnit === 'Fahrenheit' && toUnit === 'Kelvin') {
      const step1 = valueToUse - 32;
      const step2 = step1 * (5/9);
      const step3 = step2 + 273.15;
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "The Fahrenheit to Kelvin conversion bridges the gap between everyday temperature measurements in the United States and the absolute temperature scale used in scientific work worldwide.",
          
          "Daniel Fahrenheit's scale, developed in the early 18th century, was based on a mixture of ice, water, and ammonium chloride (0°F) and average human body temperature (96°F). In contrast, Lord Kelvin's scale begins at absolute zero - the lowest possible temperature in the universe.",
          
          "This conversion is particularly important in scientific and engineering applications where temperatures measured in Fahrenheit need to be converted to the standard scientific scale for calculations and international collaboration."
        ],
        fundamentalEquality: {
          statement: "-459.67°F = 0K  and  32°F = 273.15K",
          explanation: "These relationships show how absolute zero and the freezing point of water align between the Fahrenheit and Kelvin scales"
        },
        formula: {
          main: 'K = (°F - 32) × 5/9 + 273.15',
          alternate: 'K = (°F + 459.67) × 5/9'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius first",
            explanation: "Start by converting Fahrenheit to Celsius: subtract 32 and multiply by 5/9"
          },
          {
            step: "Shift to absolute scale",
            explanation: "Add 273.15 to the Celsius temperature to reach the Kelvin scale"
          },
          {
            step: "Alternative method",
            explanation: "You can also add 459.67 to Fahrenheit first (reaching the Rankine scale) and then multiply by 5/9 to get Kelvin"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°F',                                     // fromUnit
          'K = (°F - 32) × 5/9 + 273.15',          // formula
          `K = (${valueToUse} - 32) × 5/9 + 273.15 = ${finalResult}K`,  // calculation with actual values
          `${finalResult}K`                                 // result
        ),
        realWorldExample: "In materials science, when working with American manufacturing specifications that list heat treatment temperatures in Fahrenheit, scientists need to convert these to Kelvin for theoretical calculations. For example, a steel tempering temperature of 400°F would be converted to 477.59K for use in thermodynamic equations.",
        funFacts: [
          "The Rankine scale (°R) is to Fahrenheit what Kelvin is to Celsius - it starts at absolute zero but uses Fahrenheit-sized degrees.",
          "The difference between any two temperatures is the same whether measured in Rankine or Kelvin, just as it's proportional in Fahrenheit and Celsius.",
          "At approximately 574.59K (574.25°F), the numerical values on the Kelvin and Fahrenheit scales are equal."
        ],
        inputValue: valueToUse
      };
    }

    // Kelvin to Celsius
    if (fromUnit === 'Kelvin' && toUnit === 'Celsius') {
      const step1 = parseFloat(valueToUse) - 273.15;
      const finalResult = result || (step1.toFixed(2));

      return {
        introduction: [
          "The Kelvin to Celsius conversion is particularly important in scientific work, as it bridges the gap between the scientific standard (Kelvin) and everyday temperature measurements (Celsius).",
          
          "Lord Kelvin established his scale to create an absolute temperature reference, where zero represents the lowest possible temperature in the universe. This point, known as absolute zero, occurs at -273.15°C on the Celsius scale.",
          
          "The beauty of the Kelvin-Celsius relationship lies in its simplicity: both scales use the same size degrees, meaning a one-degree change in Kelvin equals a one-degree change in Celsius. Only their zero points differ."
        ],
        fundamentalEquality: {
          statement: "0K = -273.15°C  and  273.15K = 0°C",
          explanation: "These relationships show how absolute zero (0K) aligns with -273.15°C, and the freezing point of water (0°C) aligns with 273.15K"
        },
        formula: {
          main: '°C = K - 273.15',
          alternate: 'K = °C + 273.15'
        },
        derivationSteps: [
          {
            step: "Understand the scale relationship",
            explanation: "Both Kelvin and Celsius use identical degree sizes, so no multiplication factor is needed"
          },
          {
            step: "Identify the zero point offset",
            explanation: "The Kelvin scale starts at absolute zero (-273.15°C), creating a fixed offset between the scales"
          },
          {
            step: "Apply the offset conversion",
            explanation: "Simply subtract 273.15 from any Kelvin temperature to get its Celsius equivalent"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                    // value
          'K',                         // fromUnit
          '°C = K - 273.15',           // formula
          `°C = ${valueToUse} - 273.15 = ${finalResult}°C`,  // calculation with actual values
          `${finalResult}°C`                       // result
        ),
        realWorldExample: "In chemistry labs, many reactions need to be monitored at specific temperatures. While equipment might display Kelvin readings, scientists often need to convert to Celsius for practical purposes. For instance, water's triple point (where solid, liquid, and gas phases coexist) occurs at 273.16K, or 0.01°C.",
        funFacts: [
          "The Kelvin and Celsius scales are the only major temperature scales that share the same degree size.",
          "The lowest temperature ever recorded on Earth was 184K (-89.2°C) in Antarctica.",
          "Scientists have achieved temperatures just billionths of a degree above absolute zero (0K) in laboratory conditions."
        ],
        inputValue: valueToUse
      };
    }

    // Kelvin to Fahrenheit
    if (fromUnit === 'Kelvin' && toUnit === 'Fahrenheit') {
      const step1 = parseFloat(valueToUse) - 273.15;
      const step2 = step1 * (9/5);
      const step3 = step2 + 32;
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "Converting between Kelvin and Fahrenheit requires understanding both the absolute temperature scale and the historical Fahrenheit system. This conversion is particularly important in scientific applications where data needs to be communicated to a general audience in the United States.",
          
          "The Kelvin scale, starting at absolute zero (0K), represents the theoretical lowest possible temperature in the universe. This point corresponds to -459.67°F on the Fahrenheit scale, a relationship that highlights the vast difference in how these scales approach temperature measurement.",
          
          "While Kelvin is used extensively in scientific calculations, Fahrenheit remains common in everyday use in several countries. Understanding this conversion helps bridge the gap between scientific and practical applications."
        ],
        fundamentalEquality: {
          statement: "-459.67°F = 0K  and  32°F = 273.15K",
          explanation: "These relationships show how absolute zero and the freezing point of water align on both scales"
        },
        formula: {
          main: '°F = (K - 273.15) × 9/5 + 32',
          alternate: '°F = K × 9/5 - 459.67'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius first",
            explanation: "Since we know the Kelvin to Celsius relationship, start by subtracting 273.15 from the Kelvin temperature"
          },
          {
            step: "Apply the Celsius to Fahrenheit ratio",
            explanation: "Multiply by 9/5 to account for the different degree sizes between Celsius and Fahrenheit"
          },
          {
            step: "Adjust for the Fahrenheit zero point",
            explanation: "Add 32 to align with the Fahrenheit scale's zero point"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          'K',                                     // fromUnit
          '°F = (K - 273.15) × 9/5 + 32',          // formula
          `°F = (${valueToUse} - 273.15) × 9/5 + 32 = ${finalResult}°F`,  // calculation with actual values
          `${finalResult}°F`                                 // result
        ),
        realWorldExample: "In aerospace engineering, materials are often tested at extreme temperatures. When a heat shield is tested at 1000K, engineers might need to convert this to Fahrenheit (1340.33°F) to communicate the temperature stress to manufacturers who use Fahrenheit-calibrated equipment.",
        funFacts: [
          "The Kelvin to Fahrenheit conversion can also be done using the Rankine scale as an intermediate step.",
          "The temperature at which both Fahrenheit and Celsius read the same number (-40°) is about 233.15K.",
          "Room temperature (typically 20°C or 68°F) is approximately 293.15K."
        ],
        inputValue: valueToUse
      };
    }

    // Celsius to Rankine
    if (fromUnit === 'Celsius' && toUnit === 'Rankine') {
      const step1 = parseFloat(valueToUse) * (9/5);
      const step2 = step1 + 32;
      const step3 = step2 + 459.67;
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "The Celsius to Rankine conversion connects the metric temperature scale with an absolute temperature scale that uses Fahrenheit degrees. The Rankine scale, developed by William John Macquorn Rankine in 1859, is primarily used in some engineering systems in the United States.",
          
          "While the Celsius scale uses the freezing (0°C) and boiling (100°C) points of water as reference points, the Rankine scale, like Kelvin, starts at absolute zero. However, it uses Fahrenheit-sized degrees, making it useful in American engineering calculations that require an absolute scale.",
          
          "The Rankine scale's zero point (0°R) is at absolute zero, just like 0K, but it reads 491.67°R at the freezing point of water, corresponding to 0°C or 32°F."
        ],
        fundamentalEquality: {
          statement: "0°C = 491.67°R  and  100°C = 671.67°R",
          explanation: "These relationships show how the freezing and boiling points of water align on both scales"
        },
        formula: {
          main: '°R = (°C × 9/5 + 32) + 459.67',
          alternate: '°R = °C × 9/5 + 491.67'
        },
        derivationSteps: [
          {
            step: "Convert to Fahrenheit first",
            explanation: "Since Rankine uses Fahrenheit-sized degrees, first convert Celsius to Fahrenheit using (°C × 9/5 + 32)"
          },
          {
            step: "Shift to absolute scale",
            explanation: "Add 459.67 to move the Fahrenheit temperature to the absolute Rankine scale"
          },
          {
            step: "Combine steps",
            explanation: "The steps can be combined to (°C × 9/5 + 491.67), since 32 + 459.67 = 491.67"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°C',                                     // fromUnit
          '°R = (°C × 9/5 + 32) + 459.67',          // formula
          `°R = (${valueToUse} × 9/5 + 32) + 459.67 = ${finalResult}°R`,  // calculation with actual values
          `${finalResult}°R`                                 // result
        ),
        realWorldExample: "In American engineering thermodynamics, particularly in steam tables and gas calculations, the Rankine scale is used when absolute temperature is needed but Fahrenheit-sized degrees are preferred. For example, room temperature (20°C) converts to approximately 527.67°R.",
        funFacts: [
          "The Rankine scale is named after the Scottish engineer and physicist William John Macquorn Rankine.",
          "Like Kelvin, Rankine is an absolute temperature scale, meaning 0°R represents absolute zero.",
          "The temperature difference of one Rankine degree is equal to one Fahrenheit degree, just as one Kelvin equals one Celsius degree."
        ],
        inputValue: valueToUse
      };
    }

    // Rankine to Celsius
    if (fromUnit === 'Rankine' && toUnit === 'Celsius') {
      const step1 = parseFloat(valueToUse) - 459.67;
      const step2 = step1 - 32;
      const step3 = step2 * (5/9);
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "The Rankine to Celsius conversion transforms temperatures from an absolute scale used in American engineering to the everyday metric scale used worldwide. This conversion is particularly important when working with international engineering projects or converting historical American technical documentation.",
          
          "The Rankine scale, while less commonly used today, remains important in thermodynamics calculations in the United States. It shares its degree size with the Fahrenheit scale but, like Kelvin, begins at absolute zero (0°R = -459.67°F).",
          
          "Converting from Rankine to Celsius requires first understanding that 491.67°R represents the freezing point of water (0°C), and the size of one Rankine degree is equivalent to one Fahrenheit degree."
        ],
        fundamentalEquality: {
          statement: "491.67°R = 0°C  and  671.67°R = 100°C",
          explanation: "These relationships show how the water's freezing and boiling points align between the Rankine and Celsius scales"
        },
        formula: {
          main: '°C = (°R - 459.67 - 32) × 5/9',
          alternate: '°C = (°R - 491.67) × 5/9'
        },
        derivationSteps: [
          {
            step: "Convert to Fahrenheit",
            explanation: "Subtract 459.67 from the Rankine temperature to get the Fahrenheit equivalent"
          },
          {
            step: "Align with Celsius zero point",
            explanation: "Subtract 32 to align with the Celsius scale's zero point at water's freezing temperature"
          },
          {
            step: "Adjust for degree size",
            explanation: "Multiply by 5/9 to convert from Fahrenheit-sized degrees to Celsius-sized degrees"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°R',                                     // fromUnit
          '°C = (°R - 459.67 - 32) × 5/9',          // formula
          `°C = (${valueToUse} - 459.67 - 32) × 5/9 = ${finalResult}°C`,  // calculation with actual values
          `${finalResult}°C`                                   // result
        ),
        realWorldExample: "When reviewing older American engineering documents, you might encounter steam table temperatures in Rankine. For instance, a steam temperature of 672°R would need to be converted to Celsius (about 100°C) for modern international use.",
        funFacts: [
          "The Rankine scale was proposed by William John Macquorn Rankine, a Scottish mechanical engineer, in 1859.",
          "The Rankine scale is still used in some engineering calculations in the US, particularly in the aerospace industry.",
          "The relationship between Rankine and Kelvin is simple: multiply Kelvin by 1.8 to get Rankine."
        ],
        inputValue: valueToUse
      };
    }

    // Celsius to Réaumur
    if (fromUnit === 'Celsius' && toUnit === 'Réaumur') {
      const step1 = parseFloat(valueToUse) * (4/5);
      const finalResult = result || (step1.toFixed(2));

      return {
        introduction: [
          "The Celsius to Réaumur conversion connects modern temperature measurements with a historical scale that was widely used in continental Europe throughout the 18th and 19th centuries. The Réaumur scale was developed by René Antoine Ferchault de Réaumur in 1730.",
          
          "Like Celsius, the Réaumur scale uses the freezing point of water as 0°, but it sets the boiling point of water at 80° rather than 100°. This means that each degree Réaumur is 1.25 times larger than a degree Celsius.",
          
          "The Réaumur scale was particularly important in the French wine and brandy making industry, where it was used well into the 20th century. Some older French recipe books and cheese-making instructions still reference this scale."
        ],
        fundamentalEquality: {
          statement: "0°C = 0°Ré  and  100°C = 80°Ré",
          explanation: "These relationships show how both scales align at water's freezing point but differ at the boiling point"
        },
        formula: {
          main: '°Ré = °C × 4/5',
          alternate: '°Ré = °C × 0.8'
        },
        derivationSteps: [
          {
            step: "Understand the scale relationship",
            explanation: "The Réaumur scale divides the range between freezing and boiling into 80 degrees, while Celsius uses 100 degrees"
          },
          {
            step: "Calculate the conversion factor",
            explanation: "Since 80°Ré spans the same range as 100°C, we multiply by 4/5 (80/100) to convert from Celsius to Réaumur"
          },
          {
            step: "Apply the conversion",
            explanation: "Simply multiply the Celsius temperature by 4/5 to get the Réaumur equivalent"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°C',                                     // fromUnit
          '°Ré = °C × 4/5',                        // formula
          `°Ré = ${valueToUse} × 4/5 = ${finalResult}°Ré`,  // calculation with actual values
          `${finalResult}°Ré`                                 // result
        ),
        realWorldExample: "In traditional French wine making, the fermentation temperature might be specified as 16°Ré in old documentation. This would be equivalent to 20°C, which is still considered an ideal temperature for wine fermentation today.",
        funFacts: [
          "The Réaumur scale was once the predominant temperature scale in Europe, particularly in France, Germany, and Russia.",
          "Many French brewers and dairy producers continued using the Réaumur scale into the 1970s.",
          "The symbol for the Réaumur scale can be written as °Ré, °Re, °R, or simply R, though °R is now more commonly associated with the Rankine scale."
        ],
        inputValue: valueToUse
      };
    }

    // Réaumur to Celsius
    if (fromUnit === 'Réaumur' && toUnit === 'Celsius') {
      const step1 = parseFloat(valueToUse) * (5/4);
      const finalResult = result || (step1.toFixed(2));

      return {
        introduction: [
          "Imagine stumbling upon an old French wine-making manual from the 1850s, filled with precise temperature measurements in degrees Réaumur. To understand these historical instructions in modern terms, we need to convert these readings to Celsius.",
          
          "The Réaumur scale tells an interesting story of how science and industry once intersected. While it may seem peculiar today that a scale would choose 80 degrees between freezing and boiling points, this choice made practical sense for the alcohol industry - as 80 was easily divisible by many numbers, making it convenient for calculating proportions in distillation processes.",
          
          "Though largely obsolete now, understanding Réaumur conversions opens a window into the history of European craftsmanship and industrial development."
        ],
        fundamentalEquality: {
          statement: "0°Ré = 0°C  and  80°Ré = 100°C",
          explanation: "The scales share a zero point but diverge as they rise, with Réaumur using larger degree intervals"
        },
        formula: {
          main: '°C = °Ré × 5/4',
          alternate: '°C = °Ré × 1.25'
        },
        derivationSteps: [
          {
            step: "Identify the scale ratio",
            explanation: "Since 80°Ré equals 100°C, each Réaumur degree is 1.25 Celsius degrees"
          },
          {
            step: "Convert the ratio to a fraction",
            explanation: "The ratio 100/80 simplifies to 5/4, giving us our conversion factor"
          },
          {
            step: "Apply the conversion",
            explanation: "Multiply the Réaumur temperature by 5/4 to expand it to the Celsius scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°Ré',                                     // fromUnit
          '°C = °Ré × 5/4',                        // formula
          `°C = ${valueToUse} × 5/4 = ${finalResult}°C`,  // calculation with actual values
          `${finalResult}°C`                                 // result
        ),
        realWorldExample: "A 19th-century French brandy recipe might call for heating the wine to 22°Ré during distillation. Converting this to 27.5°C helps modern distillers replicate these traditional methods with contemporary equipment.",
        funFacts: [
          "The Réaumur scale was so widely used in the dairy industry that some older cheese makers still refer to it as the 'cheese makers' scale'.",
          "Russia used the Réaumur scale as its primary temperature measurement until the 1920s.",
          "Early thermometers using the Réaumur scale were often filled with alcohol rather than mercury, connecting to its origins in wine-making."
        ],
        inputValue: valueToUse
      };
    }

    // Celsius to Delisle
    if (fromUnit === 'Celsius' && toUnit === 'Delisle') {
      const step1 = 100 - parseFloat(valueToUse);
      const step2 = step1 * (3/2);
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "In 1732, in the bustling scientific community of Paris, Joseph-Nicolas Delisle created something peculiar: a temperature scale that worked backwards. As a renowned astronomer, he was more interested in measuring the cooling of hot objects than their heating, leading to this unique approach.",
          
          "Picture yourself in Delisle's observatory, tracking the temperature of cooling metals for astronomical instruments. In his world, water boils at 0° and freezes at 150° - a reverse logic that, while strange to us today, served his specific needs in studying the night sky.",
          
          "The Delisle scale found an unexpected home in Russia, where Delisle had been invited by Peter the Great. For nearly 100 years, Russian meteorologists dutifully recorded their measurements in this reverse scale, creating a fascinating challenge for modern historians analyzing historical Russian weather data."
        ],
        fundamentalEquality: {
          statement: "100°C = 0°De  and  0°C = 150°De",
          explanation: "Notice how the scale works in reverse - higher Delisle numbers indicate colder temperatures"
        },
        formula: {
          main: '°De = (100 - °C) × 3/2',
          alternate: '°De = 150 - (°C × 1.5)'
        },
        derivationSteps: [
          {
            step: "Invert the temperature difference",
            explanation: "Since Delisle works in reverse, subtract the Celsius temperature from 100"
          },
          {
            step: "Apply the scale factor",
            explanation: "Multiply by 3/2 because the Delisle scale uses 150 degrees where Celsius uses 100"
          },
          {
            step: "Understand the inverse relationship",
            explanation: "Higher Delisle values indicate colder temperatures, opposite to most temperature scales"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°C',                                     // fromUnit
          '°De = (100 - °C) × 3/2',                  // formula
          `°De = (100 - ${valueToUse}) × 3/2 = ${finalResult}°De`,  // calculation with actual values
          `${finalResult}°De`                                 // result
        ),
        realWorldExample: "When studying historical Russian weather records from the 1750s, you might find a winter temperature recorded as 175°De. This seemingly high number actually represents a quite cold -16.7°C, demonstrating the reverse nature of the scale.",
        funFacts: [
          "Delisle created his scale to study the cooling of hot metals in his astronomical work.",
          "It's the only widely used historical temperature scale that increases as temperature decreases.",
          "The scale remained in use in Russia until 1869, when they finally adopted the Celsius scale."
        ],
        inputValue: valueToUse
      };
    }

    // Delisle to Celsius
    if (fromUnit === 'Delisle' && toUnit === 'Celsius') {
      const step1 = parseFloat(valueToUse) * (2/3);
      const step2 = 100 - step1;
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "Imagine being a historian in St. Petersburg, sifting through yellowed pages of 18th-century weather records. The numbers before you seem impossibly high - 175 degrees on a winter day? Welcome to the world of the Delisle scale, where higher numbers tell tales of bitter Russian winters.",
          
          "This backwards-running scale, where 0° marks boiling water and 150° represents freezing, might seem like a mathematical puzzle today. Yet for nearly a century, this reverse logic helped Russian scientists record everything from Arctic expeditions to summer heat waves.",
          
          "Converting these historical readings to modern Celsius isn't just a mathematical exercise - it's a way of reaching across centuries to understand exactly what those early meteorologists experienced in their daily observations."
        ],
        fundamentalEquality: {
          statement: "0°De = 100°C  and  150°De = 0°C",
          explanation: "The Delisle scale runs in reverse - as Delisle readings increase, actual temperatures decrease"
        },
        formula: {
          main: '°C = 100 - (°De × 2/3)',
          alternate: '°C = 100 - (°De ÷ 1.5)'
        },
        derivationSteps: [
          {
            step: "Convert the scale factor",
            explanation: "Since 150°De equals a 100°C change, each Delisle degree represents 2/3 of a Celsius degree"
          },
          {
            step: "Account for reverse direction",
            explanation: "Subtract from 100 because the Delisle scale runs opposite to Celsius"
          },
          {
            step: "Combine the steps",
            explanation: "First multiply by 2/3 to adjust the degree size, then subtract from 100 to flip the direction"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°De',                                     // fromUnit
          '°C = 100 - (°De × 2/3)',                  // formula
          `°C = 100 - (${valueToUse} × 2/3) = ${finalResult}°C`,  // calculation with actual values
          `${finalResult}°C`                                 // result
        ),
        realWorldExample: "When decoding an 18th-century Russian weather diary that records a summer day at 50°De, we can convert this to understand they experienced a pleasant 66.7°C - a typical summer day in St. Petersburg.",
        funFacts: [
          "Russian weather records from 1746 to 1869 exclusively used the Delisle scale, creating a unique challenge for climate historians.",
          "Despite its counter-intuitive nature, the scale was particularly precise for measuring very cold temperatures.",
          "Some early Delisle thermometers were so large they had to be mounted on building walls, becoming local landmarks in Russian cities."
        ],
        inputValue: valueToUse
      };
    }

    // Celsius to Newton
    if (fromUnit === 'Celsius' && toUnit === 'Newton') {
      const step1 = parseFloat(valueToUse) * (33/100);
      const finalResult = result || (step1.toFixed(2));

      return {
        introduction: [
          "Before the thermometer as we know it today, Sir Isaac Newton sat in his laboratory in 1701, placing linseed oil in the sun and shade, watching it cool and thicken. Through these simple observations, he created one of humanity's first systematic approaches to measuring temperature.",
          
          "Newton's scale was elegantly simple - he marked 0° as the freezing point of water (just like Celsius would later do), and 33° as body temperature. While this might seem arbitrary today, it reflected Newton's belief in the power of simple, reproducible measurements.",
          
          "Though his scale didn't survive the test of time, it represents a crucial moment in scientific history: when temperature changed from a subjective feeling to a measurable quantity."
        ],
        fundamentalEquality: {
          statement: "0°C = 0°N  and  100°C = 33°N",
          explanation: "Newton's scale uses the same zero point as Celsius but compresses the range into just 33 degrees"
        },
        formula: {
          main: '°N = °C × 33/100',
          alternate: '°N = °C × 0.33'
        },
        derivationSteps: [
          {
            step: "Understand Newton's reference points",
            explanation: "Newton used the freezing point of water (0°) and human body temperature (33°) as his key points"
          },
          {
            step: "Calculate the conversion ratio",
            explanation: "Since 100°C equals 33°N, we multiply Celsius temperatures by 33/100"
          },
          {
            step: "Simplify the conversion",
            explanation: "The conversion is straightforward multiplication since both scales share a zero point"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°C',                                     // fromUnit
          '°N = °C × 33/100',                        // formula
          `°N = ${valueToUse} × 33/100 = ${finalResult}°N`,  // calculation with actual values
          `${finalResult}°N`                                 // result
        ),
        realWorldExample: "If you were reading Newton's original papers about metal working, you might find a reference to 12°N for tempering steel. This would be approximately 36.4°C in modern terms - a temperature that metalworkers today would recognize as suitable for certain tempering processes.",
        funFacts: [
          "Newton created his scale by observing how linseed oil changed consistency at different temperatures.",
          "This was one of the first attempts to create a reproducible temperature scale, predating both Fahrenheit and Celsius.",
          "Newton's scale was briefly used in scientific work in the early 18th century before being superseded by more precise scales."
        ],
        inputValue: valueToUse
      };
    }

    // Newton to Celsius
    if (fromUnit === 'Newton' && toUnit === 'Celsius') {
      const step1 = parseFloat(valueToUse) * (100/33);
      const finalResult = result || (step1.toFixed(2));

      return {
        introduction: [
          "Picture a time before mercury thermometers, before standardized scales, when measuring temperature was more art than science. In this world, a young Isaac Newton watched a spoonful of linseed oil gradually cool, noting how it thickened at different temperatures. This simple observation would lead to one of history's first temperature scales.",
          
          "Today, when we encounter Newton's temperature readings in his notebooks about optics, metallurgy, or early chemistry, we face an intriguing puzzle. His measurements, though methodical, speak a different numerical language than our modern Celsius scale.",
          
          "Converting from Newton's degrees to Celsius isn't just about mathematics - it's about bridging the gap between the dawn of systematic temperature measurement and our modern understanding of heat."
        ],
        fundamentalEquality: {
          statement: "0°N = 0°C  and  33°N = 100°C",
          explanation: "Newton's scale compresses what we know as a 100-degree Celsius range into just 33 degrees"
        },
        formula: {
          main: '°C = °N × 100/33',
          alternate: '°C = °N × 3.03'
        },
        derivationSteps: [
          {
            step: "Recognize the compression ratio",
            explanation: "Newton's 33 degrees cover the same range as Celsius's 100 degrees"
          },
          {
            step: "Expand the scale",
            explanation: "Multiply by 100/33 to stretch Newton's compressed degrees to the Celsius scale"
          },
          {
            step: "No zero point adjustment needed",
            explanation: "Both scales share the same zero point at water's freezing temperature"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°N',                                     // fromUnit
          '°C = °N × 100/33',                        // formula
          `°C = ${valueToUse} × 100/33 = ${finalResult}°C`,  // calculation with actual values
          `${finalResult}°C`                                   // result
        ),
        realWorldExample: "When studying Newton's metallurgical experiments, you might find he recorded tin melting at '9.5 degrees'. Converting this to modern Celsius (about 28.8°C) helps us understand he was observing tin becoming soft rather than actually melting, which occurs at a much higher temperature.",
        funFacts: [
          "Newton's scale was based on careful observations of how linseed oil behaved at different temperatures - a remarkably sophisticated approach for its time.",
          "His temperature scale was used by some scientists for nearly 60 years after its creation.",
          "Newton's work on temperature measurement laid the groundwork for later, more refined scales like Fahrenheit and Celsius."
        ],
        inputValue: valueToUse
      };
    }

    // Celsius to Rømer
    if (fromUnit === 'Celsius' && toUnit === 'Rømer') {
      const step1 = parseFloat(valueToUse) * (21/40);
      const step2 = step1 + 7.5;
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "In 1701, Danish astronomer Ole Rømer needed a way to calibrate his telescopes. Working in his Copenhagen observatory, he created a temperature scale using two simple reference points: salt water freezing (0°) and water boiling (60°).",
          
          "While his scale never gained widespread use outside of Denmark, it holds the distinction of being the first to use mercury in a sealed glass tube - a design that would influence thermometer making for centuries to come.",
          
          "His choice of 60 degrees between freezing and boiling wasn't random - it aligned with the base-60 number system he used in his astronomical calculations."
        ],
        fundamentalEquality: {
          statement: "0°C = 7.5°Rø  and  100°C = 60°Rø",
          explanation: "The Rømer scale starts at the freezing point of brine (about -14.3°C) and uses larger degree intervals than Celsius"
        },
        formula: {
          main: '°Rø = (°C × 21/40) + 7.5',
          alternate: '°Rø = (°C × 0.525) + 7.5'
        },
        derivationSteps: [
          {
            step: "Adjust for degree size",
            explanation: "Multiply by 21/40 because 100°C spans 52.5 Rømer degrees (60 - 7.5)"
          },
          {
            step: "Align zero points",
            explanation: "Add 7.5 to account for the difference in zero points between the scales"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°C',                                     // fromUnit
          '°Rø = (°C × 21/40) + 7.5',                  // formula
          `°Rø = (${valueToUse} × 21/40) + 7.5 = ${finalResult}°Rø`,  // calculation with actual values
          `${finalResult}°Rø`                                 // result
        ),
        realWorldExample: "Early Danish weather records from the 1710s might show a summer day as 26°Rø. Understanding this equals about 35°C helps modern climatologists reconstruct historical weather patterns.",
        funFacts: [
          "Rømer was primarily known for his groundbreaking measurement of the speed of light.",
          "His temperature scale was used in Denmark for about 50 years before being replaced by Celsius.",
          "The unusual zero point came from the coldest temperature he could achieve in his lab using a mixture of salt and ice."
        ],
        inputValue: valueToUse
      };
    }

    // Rømer to Celsius
    if (fromUnit === 'Rømer' && toUnit === 'Celsius') {
      const step1 = parseFloat(valueToUse) - 7.5;
      const step2 = step1 * (40/21);
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "Hidden in the archives of Copenhagen's old astronomical observatory lies a curious collection of weather records. The temperatures, recorded in Rømer degrees, tell the story of Danish winters and summers from three centuries ago - but reading them requires a bit of mathematical time travel.",
          
          "Ole Rømer, better known for measuring the speed of light, chose an intriguing way to mark his scale: 0° was set at the point where brine freezes, 7.5° at the freezing point of water, and 60° at water's boiling point. To modern eyes, these numbers might seem peculiar, but they made perfect sense in the world of 18th-century astronomy.",
          
          "Let's decode these historical readings and bring them into our modern Celsius world."
        ],
        fundamentalEquality: {
          statement: "7.5°Rø = 0°C  and  60°Rø = 100°C",
          explanation: "The Rømer scale uses these key points based on the behavior of salt water and pure water"
        },
        formula: {
          main: '°C = (°Rø - 7.5) × 40/21',
          alternate: '°C = (°Rø - 7.5) × 1.90476'
        },
        derivationSteps: [
          {
            step: "Adjust for zero point",
            explanation: "Subtract 7.5 to align with the Celsius zero point at water's freezing temperature"
          },
          {
            step: "Convert degree sizes",
            explanation: "Multiply by 40/21 to expand Rømer's compressed degree scale to Celsius"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°Rø',                                     // fromUnit
          '°C = (°Rø - 7.5) × 40/21',                  // formula
          `°C = (${valueToUse} - 7.5) × 40/21 = ${finalResult}°C`,  // calculation with actual values
          `${finalResult}°C`                                 // result
        ),
        realWorldExample: "An old Danish brewing manual might specify heating the mash to 30°Rø. Converting this to Celsius (about 42.9°C) reveals that these historical brewers were working at temperatures very similar to modern brewing practices.",
        funFacts: [
          "Rømer's thermometers were so precisely crafted that some survived and remained accurate for over 200 years.",
          "The unusual spacing of his scale was actually based on the sexagesimal (base-60) system used in astronomy.",
          "Despite its complexity, Rømer's scale was the first to use mercury as a measuring fluid, a practice that continued until recent times."
        ],
        inputValue: valueToUse
      };
    }

    // Réaumur to Rankine
    if (fromUnit === 'Réaumur' && toUnit === 'Rankine') {
      const step1 = parseFloat(valueToUse) * (5/4); // First to Celsius
      const step2 = step1 * (9/5) + 32; // Then to Fahrenheit
      const step3 = step2 + 459.67; // Finally to Rankine
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "What happens when French wine-making traditions meet American engineering? This unusual conversion bridges two worlds: the elegant simplicity of Réaumur's 80-degree scale, beloved by European vintners, and Rankine's absolute scale, the darling of American thermodynamics.",
          
          "These scales, born nearly a century apart, tell us something about how different cultures approached temperature measurement. While French vintners were concerned with the perfect temperature for fermenting wine, American engineers needed precise measurements for steam engines and refrigeration.",
          
          "Converting between them is like translating between two different eras of scientific thinking."
        ],
        fundamentalEquality: {
          statement: "0°Ré = 491.67°R  and  80°Ré = 671.67°R",
          explanation: "The relationship shows how Réaumur's wine-making scale aligns with Rankine's absolute temperatures"
        },
        formula: {
          main: '°R = (°Ré × 5/4 × 9/5 + 32) + 459.67',
          alternate: '°R = °Ré × 2.25 + 491.67'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius",
            explanation: "Multiply by 5/4 to convert from Réaumur to Celsius"
          },
          {
            step: "Convert to Fahrenheit",
            explanation: "Multiply by 9/5 and add 32 to reach the Fahrenheit scale"
          },
          {
            step: "Shift to absolute scale",
            explanation: "Add 459.67 to reach the Rankine absolute temperature scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°Ré',                                     // fromUnit
          '°R = (°Ré × 5/4 × 9/5 + 32) + 459.67',  // formula
          `°R = (${valueToUse} × 5/4 × 9/5 + 32) + 459.67 = ${finalResult}°R`,  // calculation with actual values
          `${finalResult}°R`                                 // result
        ),
        realWorldExample: "A historical French brewing text might specify a fermentation temperature of 16°Ré. Converting this to Rankine (527.67°R) helps modern American engineers understand the exact temperature conditions when adapting historical processes to modern equipment.",
        funFacts: [
          "While Réaumur's scale was used in wine-making, Rankine's scale was crucial in the development of steam engines.",
          "The Réaumur scale divided the range between freezing and boiling into 80 degrees because 80 was easily divisible for calculations.",
          "Both scales have largely fallen out of use, but they occasionally appear in historical documents and specialized applications."
        ],
        inputValue: valueToUse
      };
    }

    // Rankine to Réaumur
    if (fromUnit === 'Rankine' && toUnit === 'Réaumur') {
      const step1 = parseFloat(valueToUse) - 459.67; // To Fahrenheit
      const step2 = (step1 - 32) * (5/9); // To Celsius
      const step3 = step2 * (4/5); // To Réaumur
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "Steam engines and wine barrels - two worlds that rarely meet, yet both shaped how we measure temperature. When American engineers using Rankine's absolute scale need to decipher old European wine-making texts, they face an intriguing challenge.",
      
          "The Rankine scale, starting at absolute zero and climbing upward in Fahrenheit-sized steps, seems worlds apart from Réaumur's elegant 80-degree span between freezing and boiling water. Yet both scales were born from practical needs: one for understanding heat engines, the other for perfecting fermentation.",
      
          "Converting between them is like building a bridge between the Industrial Revolution and centuries-old vintner traditions."
        ],
        fundamentalEquality: {
          statement: "491.67°R = 0°Ré  and  671.67°R = 80°Ré",
          explanation: "These points show how Rankine's absolute scale aligns with Réaumur's practical brewing scale"
        },
        formula: {
          main: '°Ré = ((°R - 459.67 - 32) × 5/9) × 4/5',
          alternate: '°Ré = (°R - 491.67) × 0.44444'
        },
        derivationSteps: [
          {
            step: "Convert to Fahrenheit",
            explanation: "Subtract 459.67 from Rankine to get to the Fahrenheit scale"
          },
          {
            step: "Convert to Celsius",
            explanation: "Subtract 32 and multiply by 5/9 to reach the Celsius scale"
          },
          {
            step: "Convert to Réaumur",
            explanation: "Multiply by 4/5 to convert from Celsius to Réaumur degrees"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°R',                                     // fromUnit
          '°Ré = ((°R - 459.67 - 32) × 5/9) × 4/5',  // formula
          `°Ré = ((${valueToUse} - 459.67 - 32) × 5/9) × 4/5 = ${finalResult}°Ré`,  // calculation with actual values
          `${finalResult}°Ré`                                 // result
        ),
        realWorldExample: "A modern thermodynamics problem might give a steam temperature of 671.67°R. Converting this to Réaumur (80°Ré) reveals it's the boiling point of water - a temperature that would have been very familiar to 18th-century brewers.",
        funFacts: [
          "The Rankine scale is still used in some modern engineering calculations, while Réaumur appears mainly in historical documents.",
          "Some antique French wine cellars still have old Réaumur thermometers mounted on their walls.",
          "The Rankine scale was developed about 130 years after the Réaumur scale, showing how long different temperature standards coexisted."
        ],
        inputValue: valueToUse
      };
    } 
    if (fromUnit === 'Réaumur' && toUnit === 'Fahrenheit') {
      const step1 = parseFloat(valueToUse) * (5/4); // To Celsius
      const step2 = step1 * (9/5) + 32; // To Fahrenheit
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "Deep in the cellars of old French wineries, ancient thermometers still hang on stone walls, their Réaumur markings faded but readable. For modern American vintners studying these traditional methods, converting these readings becomes a gateway to centuries of wine-making wisdom.",
      
          "The Réaumur scale, with its convenient 80-degree span, was perfectly suited for the precise art of fermentation. Each degree represented a meaningful step in the process, making it the preferred choice of European craftsmen for generations.",
      
          "Today, as we translate these historical measurements to Fahrenheit, we're not just converting numbers – we're preserving the expertise of master vintners who perfected their craft using these carefully calibrated scales."
        ],
        fundamentalEquality: {
          statement: "0°Ré = 32°F  and  80°Ré = 212°F",
          explanation: "The Réaumur scale uses water's freezing and boiling points, just like Fahrenheit, but divides the range differently"
        },
        formula: {
          main: '°F = (°Ré × 5/4 × 9/5) + 32',
          alternate: '°F = (°Ré × 2.25) + 32'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius",
            explanation: "Multiply by 5/4 to expand Réaumur degrees to Celsius"
          },
          {
            step: "Convert to Fahrenheit",
            explanation: "Multiply by 9/5 and add 32 to reach the Fahrenheit scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°Ré',                                     // fromUnit
          '°F = (°Ré × 5/4 × 9/5) + 32',            // formula
          `°F = (${valueToUse} × 5/4 × 9/5) + 32 = ${finalResult}°F`,  // calculation with actual values
          `${finalResult}°F`                                   // result
        ),
        realWorldExample: "A 19th-century French cheese-making manual might specify aging at 12°Ré. Converting this to 59°F helps modern artisans understand why this temperature was ideal for developing specific cheese cultures.",
        funFacts: [
          "The Réaumur scale was so precise for wine-making that some French vintners continued using it into the 1980s.",
          "Many traditional European brewing and cheese-making temperatures were carefully calibrated to specific Réaumur degrees.",
          "The scale's 80-degree range made it easy to calculate quarter and half degrees, crucial for temperature-sensitive fermentation processes."
        ],
        inputValue: valueToUse
      };
    }

    // Fahrenheit to Réaumur
    if (fromUnit === 'Fahrenheit' && toUnit === 'Réaumur') {
      const step1 = parseFloat(valueToUse) - 32;
      const step2 = step1 * (5/9); // To Celsius
      const step3 = step2 * (4/5); // To Réaumur
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "When American historians stumble upon old French wine-making manuals, they face an interesting puzzle. Not only must they translate the French text, but they also need to bridge two very different ways of thinking about temperature.",
      
          "Fahrenheit's scale, born in the cold German winter of 1724, meets Réaumur's system, crafted in the sun-drenched vineyards of France. One was designed for general use, the other specifically for the art of fermentation.",
      
          "Their intersection tells us something about how science adapted to serve different needs in different places."
        ],
        fundamentalEquality: {
          statement: "32°F = 0°Ré  and  212°F = 80°Ré",
          explanation: "The Réaumur scale uses the same reference points as Celsius (water freezing and boiling) but divides the range into 80 degrees"
        },
        formula: {
          main: '°Ré = (°F - 32) × 5/9 × 4/5',
          alternate: '°Ré = (°F - 32) × 4/9'
        },
        derivationSteps: [
          {
            step: "Adjust to freezing point",
            explanation: "Subtract 32 from Fahrenheit to align with Réaumur's zero point"
          },
          {
            step: "Convert to Celsius-sized degrees",
            explanation: "Multiply by 5/9 to convert from Fahrenheit to Celsius-sized degrees"
          },
          {
            step: "Convert to Réaumur",
            explanation: "Multiply by 4/5 to compress the Celsius scale to Réaumur's 80-degree range"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°F',                                     // fromUnit
          '°Ré = (°F - 32) × 5/9 × 4/5',            // formula
          `°Ré = (${valueToUse} - 32) × 5/9 × 4/5 = ${finalResult}°Ré`,  // calculation with actual values
          `${finalResult}°Ré`                                 // result
        ),
        realWorldExample: "An American brewer trying to replicate a historical French beer recipe might encounter a fermentation temperature of 68°F. Converting this to Réaumur (about 16°Ré) helps them understand the original brewing conditions in the terms the recipe's author used.",
        funFacts: [
          "While Fahrenheit chose 96°F for body temperature, Réaumur used 26.7°Ré (80°F) for the temperature of his wine cellars.",
          "The Réaumur scale was particularly popular in cheese making, as its 80-degree range provided convenient fractions for the precise temperatures needed.",
          "Both scales were developed in the 1730s, but on opposite sides of Europe, reflecting different scientific traditions."
        ],
        inputValue: valueToUse
      };
    } 

    // ... existing code ...

    // Fahrenheit to Delisle
    if (fromUnit === 'Fahrenheit' && toUnit === 'Delisle') {
      const step1 = parseFloat(valueToUse) - 32; // To relative Fahrenheit
      const step2 = step1 * (5/9); // To Celsius
      const step3 = (100 - step2) * (3/2); // To Delisle
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "Imagine a Russian meteorologist from the 1750s and a modern American weather observer comparing notes. One records a warm spring day as 0 degrees, while the other writes down 212. Neither is wrong - they're just speaking different thermal languages.",
      
          "The Delisle scale, running backwards with zero at boiling point, seems to defy common sense to modern eyes. Yet for nearly a century, this reverse-running scale was the standard for temperature measurement across the vast Russian Empire.",
      
          "Converting between Fahrenheit and Delisle is like translating between two opposing views of how to measure the world's warmth and cold."
        ],
        fundamentalEquality: {
          statement: "212°F = 0°De  and  32°F = 150°De",
          explanation: "Notice how the Delisle scale runs in reverse - higher numbers mean colder temperatures"
        },
        formula: {
          main: '°De = (100 - ((°F - 32) × 5/9)) × 3/2',
          alternate: '°De = 150 - (°F - 32) × 5/6'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius range",
            explanation: "First subtract 32 and multiply by 5/9 to convert from Fahrenheit to Celsius"
          },
          {
            step: "Invert the scale",
            explanation: "Subtract from 100 because Delisle runs in reverse"
          },
          {
            step: "Adjust scale size",
            explanation: "Multiply by 3/2 because Delisle uses 150 degrees where Celsius uses 100"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°F',                                     // fromUnit
          '°De = (100 - ((°F - 32) × 5/9)) × 3/2',  // formula
          `°De = (100 - (${valueToUse} - 32) × 5/9)) × 3/2 = ${finalResult}°De`,  // calculation with actual values
          `${finalResult}°De`                                 // result
        ),
        realWorldExample: "When studying 18th-century Russian weather records, you might find a winter temperature of 120°De. This seemingly high number actually represents a quite cold 20°F, demonstrating how the reversed scale can initially confuse modern readers.",
        funFacts: [
          "The Delisle scale was the official temperature scale of Russia from 1746 until 1869.",
          "Despite its counter-intuitive nature, the scale was particularly good for measuring very cold temperatures, which might explain its popularity in Russia.",
          "Delisle created his scale primarily for astronomical observations, but it found its greatest use in meteorology."
        ],
        inputValue: valueToUse
      };
    }

    // ... existing code ...

    // Delisle to Fahrenheit
    if (fromUnit === 'Delisle' && toUnit === 'Fahrenheit') {
      const step1 = parseFloat(valueToUse) * (2/3); // Convert to Celsius-sized steps
      const step2 = 100 - step1; // Invert to get Celsius
      const step3 = step2 * (9/5) + 32; // Convert to Fahrenheit
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "In the frosty archives of St. Petersburg, centuries-old weather records tell their stories in a curious way. A summer day might be marked as 50 degrees, while the depths of winter show readings of 150 or more. Welcome to the world of Delisle, where lower numbers mean warmer days.",
      
          "For modern meteorologists studying climate history, these reversed readings present an intriguing puzzle. A temperature that sounds scorching hot in Fahrenheit might actually represent a bitter Russian winter in Delisle's backwards-running scale.",
      
          "Let's unlock these historical records and translate them into today's familiar Fahrenheit readings."
        ],
        fundamentalEquality: {
          statement: "0°De = 212°F  and  150°De = 32°F",
          explanation: "The Delisle scale runs in reverse, with 0° at water's boiling point and 150° at freezing"
        },
        formula: {
          main: '°F = ((100 - (°De × 2/3)) × 9/5) + 32',
          alternate: '°F = 212 - (°De × 6/5)'
        },
        derivationSteps: [
          {
            step: "Convert Delisle intervals",
            explanation: "Multiply by 2/3 to convert Delisle's 150-degree span to Celsius's 100-degree span"
          },
          {
            step: "Invert the scale",
            explanation: "Subtract from 100 to flip Delisle's reverse numbering to match Celsius's direction"
          },
          {
            step: "Convert to Fahrenheit",
            explanation: "Multiply by 9/5 and add 32 to reach the Fahrenheit scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°De',                                     // fromUnit
          '°F = ((100 - (°De × 2/3)) × 9/5) + 32',  // formula
          `°F = (100 - (${valueToUse} × 2/3)) × 9/5 + 32 = ${finalResult}°F`,  // calculation with actual values
          `${finalResult}°F`                                 // result
        ),
        realWorldExample: "When a Russian naval log from 1750 records a temperature of 25°De, modern historians can convert this to about 167°F, revealing a hot summer day in the port of St. Petersburg.",
        funFacts: [
          "Delisle originally created his scale for astronomical observations, not weather recording.",
          "The reverse nature of the scale made it particularly precise for measuring very cold temperatures, which may explain its long use in Russia.",
          "Some Russian scientists preferred Delisle's scale because its negative numbers were rarely needed, even in the coldest Siberian winters."
        ],
        inputValue: valueToUse
      };
    }


    // ... existing code ...

    // Fahrenheit to Newton
    if (fromUnit === 'Fahrenheit' && toUnit === 'Newton') {
      const step1 = parseFloat(valueToUse) - 32; // To relative Fahrenheit
      const step2 = step1 * (5/9); // To Celsius
      const step3 = step2 * (33/100); // To Newton
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "In 1701, while experimenting with linseed oil and metal ingots, Isaac Newton created something remarkable: the world's first systematic temperature scale. Today, when we convert our familiar Fahrenheit readings to Newton's scale, we're reaching back to that moment when temperature first became more than just 'hot' or 'cold'.",
      
          "Newton's elegant scale used just 33 degrees between freezing and boiling water, each degree marking a distinct change in materials he observed. Where we might say 212°F for boiling water, Newton simply wrote 33° - a beautiful simplicity born from careful observation.",
      
          "Though his scale didn't survive, it marked humanity's first steps toward understanding heat as something we could measure precisely."
        ],
        fundamentalEquality: {
          statement: "32°F = 0°N  and  212°F = 33°N",
          explanation: "Newton's scale compresses the Fahrenheit range of 180 degrees into just 33 degrees"
        },
        formula: {
          main: '°N = ((°F - 32) × 5/9) × 33/100',
          alternate: '°N = (°F - 32) × 0.1833'
        },
        derivationSteps: [
          {
            step: "Align zero points",
            explanation: "Subtract 32 to match Newton's zero at water's freezing point"
          },
          {
            step: "Convert to Celsius-sized steps",
            explanation: "Multiply by 5/9 to convert from Fahrenheit intervals to Celsius"
          },
          {
            step: "Scale to Newton degrees",
            explanation: "Multiply by 33/100 to compress to Newton's 33-degree scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°F',                                     // fromUnit
          '°N = ((°F - 32) × 5/9) × 33/100',        // formula
          `°N = (${valueToUse} - 32) × 5/9) × 33/100 = ${finalResult}°N`,  // calculation with actual values
          `${finalResult}°N`                                 // result
        ),
        realWorldExample: "When reading Newton's original papers, you might find he recorded a warm summer day as 11°N. Converting from our modern scale shows this equals about 100°F - helping us understand exactly what weather conditions he was observing.",
        funFacts: [
          "Newton developed his scale by observing how linseed oil changed consistency at different temperatures.",
          "This was one of the first attempts to create a reproducible temperature scale, predating both Fahrenheit and Celsius.",
          "Newton's scale was briefly used in scientific work in the early 18th century before being superseded by more precise scales."
        ],
        inputValue: valueToUse
      };
    }

    // ... existing code ...

    // Fahrenheit to Newton
    if (fromUnit === 'Fahrenheit' && toUnit === 'Newton') {
      const step1 = parseFloat(valueToUse) - 32; // To relative Fahrenheit
      const step2 = step1 * (5/9); // To Celsius
      const step3 = step2 * (33/100); // To Newton
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "In 1701, while experimenting with linseed oil and metal ingots, Isaac Newton created something remarkable: the world's first systematic temperature scale. Today, when we convert our familiar Fahrenheit readings to Newton's scale, we're reaching back to that moment when temperature first became more than just 'hot' or 'cold'.",
      
          "Newton's elegant scale used just 33 degrees between freezing and boiling water, each degree marking a distinct change in materials he observed. Where we might say 212°F for boiling water, Newton simply wrote 33° - a beautiful simplicity born from careful observation.",
      
          "Though his scale didn't survive, it marked humanity's first steps toward understanding heat as something we could measure precisely."
        ],
        fundamentalEquality: {
          statement: "32°F = 0°N  and  212°F = 33°N",
          explanation: "Newton's scale compresses the Fahrenheit range of 180 degrees into just 33 degrees"
        },
        formula: {
          main: '°N = ((°F - 32) × 5/9) × 33/100',
          alternate: '°N = (°F - 32) × 0.1833'
        },
        derivationSteps: [
          {
            step: "Align zero points",
            explanation: "Subtract 32 to match Newton's zero at water's freezing point"
          },
          {
            step: "Convert to Celsius-sized steps",
            explanation: "Multiply by 5/9 to convert from Fahrenheit intervals to Celsius"
          },
          {
            step: "Scale to Newton degrees",
            explanation: "Multiply by 33/100 to compress to Newton's 33-degree scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°F',                                     // fromUnit
          '°N = ((°F - 32) × 5/9) × 33/100',        // formula
          `°N = (${valueToUse} - 32) × 5/9) × 33/100 = ${finalResult}°N`,  // calculation with actual values
          `${finalResult}°N`                                 // result
        ),
        realWorldExample: "When reading Newton's original papers, you might find he recorded a warm summer day as 11°N. Converting from our modern scale shows this equals about 100°F - helping us understand exactly what weather conditions he was observing.",
        funFacts: [
          "Newton developed his scale by observing how linseed oil changed consistency with temperature.",
          "His scale was the first to use consistent, reproducible reference points for temperature measurement.",
          "The 33-degree range was likely chosen because it made calculations simpler in an era before calculators."
        ],
        inputValue: valueToUse
      };
    }

    // ... existing code ...

    // Newton to Fahrenheit
    if (fromUnit === 'Newton' && toUnit === 'Fahrenheit') {
      const step1 = parseFloat(valueToUse) * (100/33); // To Celsius
      const step2 = step1 * (9/5) + 32; // To Fahrenheit
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "Before mercury thermometers, before standardized scales, there was Newton in his laboratory, watching linseed oil thicken in the cold and thin in the heat. His simple 33-degree scale, created decades before Fahrenheit's more famous system, marked the beginning of precise temperature measurement.",
      
          "When we convert from Newton's degrees to our modern Fahrenheit scale, we're translating between two very different ways of seeing heat: Newton's direct observations of nature versus Fahrenheit's carefully calibrated system.",
      
          "Each degree on Newton's scale told a story about how materials changed with temperature - a practical approach that would influence thermometry for generations."
        ],
        fundamentalEquality: {
          statement: "0°N = 32°F  and  33°N = 212°F",
          explanation: "Newton's compact 33-degree scale maps to Fahrenheit's wider 180-degree range between freezing and boiling"
        },
        formula: {
          main: '°F = (°N × 100/33 × 9/5) + 32',
          alternate: '°F = (°N × 5.45454) + 32'
        },
        derivationSteps: [
          {
            step: "Expand Newton's scale",
            explanation: "Multiply by 100/33 to convert Newton's compressed scale to Celsius-sized degrees"
          },
          {
            step: "Convert to Fahrenheit",
            explanation: "Multiply by 9/5 and add 32 to reach the Fahrenheit scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°N',                                     // fromUnit
          '°F = (°N × 100/33 × 9/5) + 32',          // formula
          `°F = (${valueToUse} × 100/33 × 9/5) + 32 = ${finalResult}°F`,  // calculation with actual values
          `${finalResult}°F`                                   // result
        ),
        realWorldExample: "In Newton's notes, he recorded that beeswax melts at 7.5°N. Converting this to modern Fahrenheit (about 98°F) shows how precise his observations were, as this matches our current understanding of beeswax's melting point.",
        funFacts: [
          "Newton's scale was based on direct observation of material changes, making it one of the first empirical temperature scales.",
          "He chose linseed oil for his measurements because it expanded and contracted more visibly than other liquids available to him.",
          "Though his scale was eventually superseded, Newton's method of using fixed points for calibration influenced all later temperature scales."
        ],
        inputValue: valueToUse
      };
    }


    // Fahrenheit to Rømer
    if (fromUnit === 'Fahrenheit' && toUnit === 'Rømer') {
      const step1 = parseFloat(valueToUse) - 32; // To relative Fahrenheit
      const step2 = step1 * (5/9); // To Celsius
      const step3 = step2 * (21/40) + 7.5; // To Rømer
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "In the old Copenhagen observatory, where astronomers once tracked the stars through brass telescopes, Ole Rømer created a temperature scale that would influence Danish science for half a century. His unique choice of reference points - salt water freezing at 0° and water boiling at 60° - reflected the practical needs of astronomical observation.",
      
          "Converting between Fahrenheit and Rømer bridges two different approaches to temperature: Rømer's astronomical precision versus Fahrenheit's more practical everyday scale. While Fahrenheit chose human body temperature as a key reference, Rømer looked to the stars.",
      
          "Each degree on Rømer's scale was carefully chosen to help calibrate the delicate instruments of 18th-century astronomy."
        ],
        fundamentalEquality: {
          statement: "32°F = 7.5°Rø  and  212°F = 60°Rø",
          explanation: "Rømer's scale starts at the freezing point of brine and uses larger degree intervals than Fahrenheit"
        },
        formula: {
          main: '°Rø = ((°F - 32) × 5/9 × 21/40) + 7.5',
          alternate: '°Rø = (°F - 32) × 0.2625 + 7.5'
        },
        derivationSteps: [
          {
            step: "Convert to relative scale",
            explanation: "Subtract 32 to align with the Celsius zero point"
          },
          {
            step: "Convert to Celsius",
            explanation: "Multiply by 5/9 to convert to Celsius-sized degrees"
          },
          {
            step: "Convert to Rømer",
            explanation: "Multiply by 21/40 and add 7.5 to reach Rømer's scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°F',                                     // fromUnit
          '°Rø = ((°F - 32) × 5/9 × 21/40) + 7.5',    // formula
          `°Rø = (${valueToUse} - 32) × 5/9 × 21/40 + 7.5 = ${finalResult}°Rø`,  // calculation with actual values
          `${finalResult}°Rø`                                 // result
        ),
        realWorldExample: "A modern weather report showing 68°F would read as about 23°Rø on Rømer's scale - a temperature that Danish astronomers would have recognized as ideal for telescope observations.",
        funFacts: [
          "Rømer's scale was the first to use mercury as a thermometric fluid, a practice that continued for over 250 years.",
          "The unusual 7.5° freezing point came from Rømer's use of a salt-ice mixture as his zero point.",
          "His scale was particularly popular in Denmark and Norway, where it was used for weather records until the mid-1800s."
        ],
        inputValue: valueToUse
      };
    }

    // ... existing code ...

    // Rømer to Fahrenheit
    if (fromUnit === 'Rømer' && toUnit === 'Fahrenheit') {
      const step1 = parseFloat(valueToUse) - 7.5; // Adjust zero point
      const step2 = step1 * (40/21); // To Celsius
      const step3 = step2 * (9/5) + 32; // To Fahrenheit
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "Among the dusty archives of Copenhagen's Round Tower observatory lies a remarkable legacy: temperature records that helped astronomers track how their telescopes behaved in different weather. These readings, in Rømer degrees, tell stories of clear winter nights and warm summer observations.",
      
          "Ole Rømer, who first measured the speed of light, knew that precise temperature measurements were crucial for astronomical instruments. His scale, with its peculiar 7.5° freezing point and 60° boiling point, was designed to match the mathematical tables astronomers used.",
      
          "Converting these historical readings to Fahrenheit helps us understand the conditions under which some of astronomy's great discoveries were made."
        ],
        fundamentalEquality: {
          statement: "7.5°Rø = 32°F  and  60°Rø = 212°F",
          explanation: "Rømer's scale uses larger degree intervals and starts at a lower point than Fahrenheit"
        },
        formula: {
          main: '°F = ((°Rø - 7.5) × 40/21 × 9/5) + 32',
          alternate: '°F = (°Rø - 7.5) × 3.42857 + 32'
        },
        derivationSteps: [
          {
            step: "Align zero points",
            explanation: "Subtract 7.5 to match Rømer's scale with the Celsius zero point"
          },
          {
            step: "Convert to Celsius",
            explanation: "Multiply by 40/21 to expand Rømer's compressed scale to Celsius"
          },
          {
            step: "Convert to Fahrenheit",
            explanation: "Multiply by 9/5 and add 32 to reach the Fahrenheit scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°Rø',                                     // fromUnit
          '°F = ((°Rø - 7.5) × 40/21 × 9/5) + 32',    // formula
          `°F = (${valueToUse} - 7.5) × 40/21 × 9/5 + 32 = ${finalResult}°F`,  // calculation with actual values
          `${finalResult}°F`                                 // result
        ),
        realWorldExample: "An old Danish astronomical record might note that a telescope was calibrated at 15°Rø. Converting this to about 52°F helps modern astronomers understand the precise conditions under which historical observations were made.",
        funFacts: [
          "Rømer's thermometers were so well-made that some survived and remained accurate for over 200 years.",
          "His scale was particularly useful for astronomical calculations because it used the same base-60 system as astronomical angles.",
          "The seemingly odd 7.5° freezing point actually made perfect sense in the context of astronomical calculations of the time."
        ],
        inputValue: valueToUse
      };
    }

    // ... existing code ...

    // Kelvin to Réaumur
    if (fromUnit === 'Kelvin' && toUnit === 'Réaumur') {
      const step1 = parseFloat(valueToUse) - 273.15; // To Celsius
      const step2 = step1 * (4/5); // To Réaumur
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "When modern physicists meet historical vintners, interesting temperature translations emerge. Kelvin's absolute scale, born from thermodynamics and quantum mechanics, seems worlds apart from Réaumur's practical wine-making measurements.",
      
          "Yet both scales tell us something important about temperature: Kelvin reveals the fundamental nature of heat, while Réaumur shows us how craftsmen understood it through their daily work. One starts at absolute zero, the other at water's freezing point.",
      
          "Converting between them bridges the gap between theoretical physics and centuries of practical fermentation expertise."
        ],
        fundamentalEquality: {
          statement: "273.15K = 0°Ré  and  373.15K = 80°Ré",
          explanation: "Réaumur's scale divides the range between freezing and boiling water into 80 degrees, while Kelvin measures from absolute zero"
        },
        formula: {
          main: '°Ré = (K - 273.15) × 4/5',
          alternate: '°Ré = (K - 273.15) × 0.8'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius",
            explanation: "Subtract 273.15 to convert from Kelvin to Celsius"
          },
          {
            step: "Convert to Réaumur",
            explanation: "Multiply by 4/5 to convert from Celsius to Réaumur's compressed scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          'K',                                     // fromUnit
          '°Ré = (K - 273.15) × 4/5',                  // formula
          `°Ré = (${valueToUse} - 273.15) × 4/5 = ${finalResult}°Ré`,  // calculation with actual values
          `${finalResult}°Ré`                                 // result
        ),
        realWorldExample: "A physicist studying wine fermentation might need to convert a modern temperature control setting of 293.15K to Réaumur (16°Ré) to compare with historical wine-making manuals.",
        funFacts: [
          "While Kelvin is used in modern physics, Réaumur's scale was once the standard for French wine-making.",
          "The Réaumur scale was designed to make fractional degrees easy to calculate, important for precise fermentation control.",
          "Some antique wine cellars still have Réaumur thermometers mounted on their walls, alongside modern Kelvin-calibrated equipment."
        ],
        inputValue: valueToUse
      };
    }


    // Réaumur to Kelvin
    if (fromUnit === 'Réaumur' && toUnit === 'Kelvin') {
      const step1 = parseFloat(valueToUse) * (5/4); // To Celsius
      const step2 = step1 + 273.15; // To Kelvin
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "In the dim cellars of old French wineries, Réaumur thermometers marked the perfect temperatures for aging wine. Today, in modern physics laboratories, Kelvin measurements track the motion of individual atoms. These two worlds - separated by centuries but united by the science of heat - speak different temperature languages.",
      
          "While Réaumur's 80-degree scale helped craftsmen perfect their fermentation, Kelvin's absolute scale helps us understand the fundamental nature of thermal energy. Converting between them connects the art of traditional wine-making with the precision of modern thermodynamics.",
      
          "Each degree Réaumur represents centuries of practical knowledge; each Kelvin, the march toward absolute zero."
        ],
        fundamentalEquality: {
          statement: "0°Ré = 273.15K  and  80°Ré = 373.15K",
          explanation: "The Réaumur scale uses water's freezing and boiling points, while Kelvin starts from absolute zero"
        },
        formula: {
          main: 'K = (°Ré × 5/4) + 273.15',
          alternate: 'K = °Ré × 1.25 + 273.15'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius",
            explanation: "Multiply by 5/4 to expand Réaumur's compressed scale to Celsius's 100-degree scale"
          },
          {
            step: "Convert to Kelvin",
            explanation: "Add 273.15 to shift from Celsius to the absolute Kelvin scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°Ré',                                     // fromUnit
          'K = (°Ré × 5/4) + 273.15',                // formula
          `K = (${valueToUse} × 5/4) + 273.15 = ${finalResult}K`,  // calculation with actual values
          `${finalResult}K`                                 // result
        ),
        realWorldExample: "When a historical brewing text specifies a fermentation temperature of 12°Ré, modern brewers can convert this to 288.15K to set their temperature-controlled fermentation tanks.",
        funFacts: [
          "Réaumur's scale was based on the expansion of alcohol, while Kelvin's scale is based on fundamental thermodynamic principles.",
          "The Réaumur scale's 80-degree range made it perfect for crafts requiring precise temperature control.",
          "While Kelvin eliminated negative temperatures, Réaumur embraced them for measuring winter conditions in wine cellars."
        ],
        inputValue: valueToUse
      };
    }


    // Kelvin to Delisle
    if (fromUnit === 'Kelvin' && toUnit === 'Delisle') {
      const step1 = parseFloat(valueToUse) - 273.15; // To Celsius
      const step2 = (100 - step1) * (3/2); // To Delisle
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "Imagine bridging two opposite views of temperature: Kelvin's scale, climbing upward from the absolute coldness of space, and Delisle's scale, counting downward from the heat of boiling water. One scale born in the quantum age, the other in the candlelit observatories of 18th-century Russia.",
      
          "While modern physicists use Kelvin to measure the subtle vibrations of atoms, Delisle designed his reverse scale for tracking the frigid temperatures of Siberian winters. Where Kelvin starts at absolute zero and rises, Delisle begins at boiling water and counts higher as things get colder.",
      
          "Converting between them is like translating between two opposing philosophies of measuring nature's heat and cold."
        ],
        fundamentalEquality: {
          statement: "373.15K = 0°De  and  273.15K = 150°De",
          explanation: "As Kelvin rises from absolute zero, Delisle counts backward from boiling water"
        },
        formula: {
          main: '°De = (100 - (K - 273.15)) × 3/2',
          alternate: '°De = (373.15 - K) × 1.5'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius",
            explanation: "Subtract 273.15 to convert from Kelvin to Celsius"
          },
          {
            step: "Invert and scale",
            explanation: "Subtract from 100 and multiply by 3/2 to reach Delisle's reverse scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          'K',                                     // fromUnit
          '°De = (100 - (K - 273.15)) × 3/2',        // formula
          `°De = (100 - (${valueToUse} - 273.15)) × 3/2 = ${finalResult}°De`,  // calculation with actual values
          `${finalResult}°De`                                 // result
        ),
        realWorldExample: "A modern laboratory might record liquid nitrogen at 77K. Converting this to Delisle (about 442°De) shows why this reverse scale was particularly useful for very cold temperatures - higher numbers meant colder conditions.",
        funFacts: [
          "While Kelvin is based on absolute zero, Delisle chose boiling water as his zero point.",
          "The Delisle scale was the official temperature scale of Russia for over a century.",
          "Delisle's reverse numbering actually made sense for astronomical observations, where higher numbers often meant less of something."
        ],
        inputValue: valueToUse
      };
    }

    // Delisle to Kelvin
    if (fromUnit === 'Delisle' && toUnit === 'Kelvin') {
      const step1 = parseFloat(valueToUse) * (2/3); // Convert to Celsius-sized steps
      const step2 = 100 - step1; // Invert to get Celsius
      const step3 = step2 + 273.15; // Convert to Kelvin
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "In the vast archives of Russian science, temperature records tell their stories backwards. A Delisle reading of 150 marks freezing water, while 0 indicates its boiling point - a reverse logic that served Russian scientists for over a century. Today, we translate these historical measurements into Kelvin, where every degree marks an absolute measure of molecular motion.",
      
          "This conversion bridges not just different scales, but different ways of thinking about heat and cold. Delisle, working in the bitter cold of St. Petersburg, created a scale that made sense for measuring extreme cold. Kelvin, working with the fundamental laws of thermodynamics, created one that made sense of the universe itself.",
      
          "When we convert from Delisle to Kelvin, we're not just changing numbers - we're translating between two different scientific worldviews."
        ],
        fundamentalEquality: {
          statement: "0°De = 373.15K  and  150°De = 273.15K",
          explanation: "Delisle's reverse-running scale meets Kelvin's absolute measurements at these key points"
        },
        formula: {
          main: 'K = (100 - (°De × 2/3)) + 273.15',
          alternate: 'K = 373.15 - (°De × 2/3)'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius-sized intervals",
            explanation: "Multiply by 2/3 to convert from Delisle's 150-degree span to Celsius's 100-degree span"
          },
          {
            step: "Invert the scale",
            explanation: "Subtract from 100 to flip Delisle's reverse numbering to match Celsius's direction"
          },
          {
            step: "Convert to Kelvin",
            explanation: "Add 273.15 to shift from Celsius to the absolute Kelvin scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°De',                                     // fromUnit
          'K = (100 - (°De × 2/3)) + 273.15',        // formula
          `K = (100 - (${valueToUse} × 2/3)) + 273.15 = ${finalResult}K`,  // calculation with actual values
          `${finalResult}K`                                 // result
        ),
        realWorldExample: "When an 18th-century Russian meteorologist recorded a winter temperature of 180°De, modern scientists can convert this to about 253K, revealing just how cold those historical winters were.",
        funFacts: [
          "Delisle's scale was particularly popular in Russia because it rarely required negative numbers, even in Siberian winters.",
          "The transition from Delisle to Kelvin in Russian science marked a shift from practical to theoretical temperature measurement.",
          "Some Russian scientific papers from the 1800s included both Delisle and early absolute temperature measurements, bridging the old and new systems."
        ],
        inputValue: valueToUse
      };
    }
    // Kelvin to Newton
    if (fromUnit === 'Kelvin' && toUnit === 'Newton') {
      const step1 = parseFloat(valueToUse) - 273.15; // To Celsius
      const step2 = step1 * (33/100); // To Newton
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "From Newton's candlelit laboratory to modern physics facilities, the quest to understand heat has driven scientific progress. While Newton observed linseed oil's behavior to create his 33-degree scale, today's scientists use Kelvin to measure the quantum dance of atoms.",
      
          "Converting between these scales bridges nearly 300 years of thermal science. Newton's simple scale, marking the first systematic attempt to measure temperature, meets Kelvin's absolute scale, which reveals the fundamental nature of heat itself.",
      
          "Each Newton degree represented a visible change in materials; each Kelvin represents an absolute measure of molecular motion - two ways of seeing the same thermal truth."
        ],
        fundamentalEquality: {
          statement: "273.15K = 0°N  and  373.15K = 33°N",
          explanation: "Newton's compact 33-degree scale maps to the 100-degree range between freezing and boiling in Kelvin"
        },
        formula: {
          main: '°N = (K - 273.15) × 33/100',
          alternate: '°N = (K - 273.15) × 0.33'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius",
            explanation: "Subtract 273.15 to shift from absolute zero to the Celsius scale"
          },
          {
            step: "Scale to Newton",
            explanation: "Multiply by 33/100 to compress to Newton's 33-degree range"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          'K',                                     // fromUnit
          '°N = (K - 273.15) × 33/100',            // formula
          `°N = (${valueToUse} - 273.15) × 33/100 = ${finalResult}°N`,  // calculation with actual values
          `${finalResult}°N`                                 // result
        ),
        realWorldExample: "A modern physicist measuring a phase transition at 303.15K can convert this to Newton's scale (about 9.9°N) to understand how Newton might have observed and recorded similar phenomena in his time.",
        funFacts: [
          "Newton created his scale 150 years before scientists understood the concept of absolute zero.",
          "His scale was based on careful observation of physical changes, while Kelvin's is based on the fundamental behavior of matter.",
          "Newton's linseed oil thermometer and modern Kelvin-calibrated instruments would agree on the direction of temperature change, just not the size of the degrees."
        ],
        inputValue: valueToUse
      };
    }


    // Newton to Kelvin
    if (fromUnit === 'Newton' && toUnit === 'Kelvin') {
      const step1 = parseFloat(valueToUse) * (100/33); // To Celsius
      const step2 = step1 + 273.15; // To Kelvin
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "In 1701, by the flickering light of candles, Isaac Newton watched linseed oil thicken and thin with temperature changes, creating mankind's first systematic temperature scale. Today, we can translate his meticulous observations into the language of modern physics - the Kelvin scale.",
      
          "While Newton counted degrees by watching materials change, Lord Kelvin would later define temperature through the fundamental motion of molecules. Converting between these scales bridges two revolutionary moments in science: Newton's empirical genius and Kelvin's theoretical insight.",
      
          "Each Newton degree tells a story of careful observation; each Kelvin reveals the underlying dance of atoms that Newton could only dream of understanding."
        ],
        fundamentalEquality: {
          statement: "0°N = 273.15K  and  33°N = 373.15K",
          explanation: "Newton's compact 33-degree scale expands to match Kelvin's measurement from absolute zero"
        },
        formula: {
          main: 'K = (°N × 100/33) + 273.15',
          alternate: 'K = °N × 3.03030 + 273.15'
        },
        derivationSteps: [
          {
            step: "Expand Newton's scale",
            explanation: "Multiply by 100/33 to convert from Newton's compressed scale to Celsius-sized degrees"
          },
          {
            step: "Shift to absolute scale",
            explanation: "Add 273.15 to move from the Celsius reference point to absolute zero"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°N',                                     // fromUnit
          'K = (°N × 100/33) + 273.15',            // formula
          `K = (${valueToUse} × 100/33) + 273.15 = ${finalResult}K`,  // calculation with actual values
          `${finalResult}K`                                 // result
        ),
        realWorldExample: "When Newton recorded that metals began to glow at 25°N, we can now convert this to about 348.15K, matching modern measurements of the temperature at which metals begin to emit visible light.",
        funFacts: [
          "Newton's scale was remarkably accurate for its time, despite being based purely on observation.",
          "The conversion between Newton and Kelvin reveals how precise his measurements were, often matching modern values within a few degrees.",
          "Newton's approach of using observable changes to mark temperatures laid the groundwork for later absolute temperature scales."
        ],
        inputValue: valueToUse
      };
    }

    // Kelvin to Rømer
    if (fromUnit === 'Kelvin' && toUnit === 'Rømer') {
      const step1 = parseFloat(valueToUse) - 273.15; // To Celsius
      const step2 = step1 * (21/40) + 7.5; // To Rømer
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "From the ancient observatories of Copenhagen to modern physics laboratories, the story of temperature measurement spans centuries. Ole Rømer, who first measured the speed of light, created his scale to help astronomers calibrate their instruments. Today's Kelvin scale measures the very quantum vibrations he could only imagine.",
      
          "While Rømer marked his zero at the freezing point of brine, chosen for astronomical calculations, Kelvin found nature's true zero in the absolute stillness of atomic motion. Their scales tell two stories of scientific discovery: one through the lens of the stars, the other through the dance of molecules.",
      
          "Converting between them bridges the gap between classical astronomy and quantum physics."
        ],
        fundamentalEquality: {
          statement: "273.15K = 7.5°Rø  and  373.15K = 60°Rø",
          explanation: "Rømer's scale, designed for astronomical work, maps to Kelvin's absolute measurements with an offset of 7.5 degrees"
        },
        formula: {
          main: '°Rø = (K - 273.15) × 21/40 + 7.5',
          alternate: '°Rø = (K - 273.15) × 0.525 + 7.5'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius",
            explanation: "Subtract 273.15 to shift from absolute zero to the Celsius scale"
          },
          {
            step: "Convert to Rømer",
            explanation: "Multiply by 21/40 and add 7.5 to reach Rømer's astronomical scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          'K',                                     // fromUnit
          '��Rø = (K - 273.15) × 21/40 + 7.5',      // formula
          `°Rø = (${valueToUse} - 273.15) × 21/40 + 7.5 = ${finalResult}°Rø`,  // calculation with actual values
          `${finalResult}°Rø`                                 // result
        ),
        realWorldExample: "A modern physicist studying stellar atmospheres at 5778K (the Sun's surface temperature) might convert this to Rømer's scale (about 2893°Rø) to compare with historical astronomical observations.",
        funFacts: [
          "Rømer's scale was the first to use mercury thermometers, a technology that would dominate temperature measurement for two centuries.",
          "His unusual choice of 7.5° for the freezing point was designed to eliminate negative numbers in astronomical calculations.",
          "While Kelvin's scale is based on absolute zero, Rømer's was based on practical astronomical needs."
        ],
        inputValue: valueToUse
      };
    }

    // Rømer to Kelvin
    if (fromUnit === 'Rømer' && toUnit === 'Kelvin') {
      const step1 = parseFloat(valueToUse) - 7.5; // Adjust for Rømer's zero point
      const step2 = step1 * (40/21); // Convert to Celsius
      const step3 = step2 + 273.15; // Convert to Kelvin
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "In the torch-lit chambers of Copenhagen's Round Tower Observatory, Ole Rømer once calibrated his mercury thermometers against the night sky. Today, in the gleaming laboratories of quantum physics, scientists measure temperature in terms of molecular motion. These two worlds, separated by centuries, meet in our temperature conversions.",
      
          "Rømer's scale, designed for precise astronomical measurements, used the freezing point of brine as its zero - a practical choice for observatory work. Kelvin's scale, reaching down to the absolute motionlessness of matter, reveals the quantum nature of heat that Rømer could never have imagined.",
      
          "Converting from Rømer to Kelvin traces the evolution of our understanding from the age of classical astronomy to the quantum era."
        ],
        fundamentalEquality: {
          statement: "7.5°Rø = 273.15K  and  60°Rø = 373.15K",
          explanation: "Rømer's astronomical scale, with its 7.5° offset, maps to Kelvin's absolute measurements"
        },
        formula: {
          main: 'K = ((°Rø - 7.5) × 40/21) + 273.15',
          alternate: 'K = (°Rø - 7.5) × 1.904762 + 273.15'
        },
        derivationSteps: [
          {
            step: "Adjust zero point",
            explanation: "Subtract 7.5 to align with the Celsius scale's zero point"
          },
          {
            step: "Convert to Celsius",
            explanation: "Multiply by 40/21 to expand Rømer's compressed scale"
          },
          {
            step: "Convert to Kelvin",
            explanation: "Add 273.15 to shift to the absolute temperature scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°Rø',                                     // fromUnit
          'K = ((°Rø - 7.5) × 40/21) + 273.15',      // formula
          `K = (${valueToUse} - 7.5) × 40/21 + 273.15 = ${finalResult}K`,  // calculation with actual values
          `${finalResult}K`                                 // result
        ),
        realWorldExample: "When historical records show Rømer measuring a comet's tail at 15°Rø, modern astronomers can convert this to 287.15K to understand the precise observing conditions of that historic night.",
        funFacts: [
          "Rømer's original thermometers were so precise that they remained accurate for astronomical observations for over a century.",
          "His scale's unusual fractions made perfect sense for astronomical calculations in an era before electronic computers.",
          "The conversion between Rømer and Kelvin spans the entire history of modern temperature measurement, from astronomy to quantum mechanics."
        ],
        inputValue: valueToUse
      };
    }

    // Réaumur to Rømer
    if (fromUnit === 'Réaumur' && toUnit === 'Rømer') {
      const step1 = parseFloat(valueToUse) * (5/4); // To Celsius
      const step2 = step1 * (21/40) + 7.5; // To Rømer
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "In the wine cellars of France and the observatories of Denmark, two different ways of measuring temperature evolved side by side. Réaumur's thermometers helped vintners perfect their craft, while Rømer's instruments helped astronomers map the heavens.",
      
          "These two scales, born from different needs but sharing the same scientific spirit, tell us about a time when European science was finding its own identity. Réaumur divided the span between freezing and boiling into 80 degrees for precise wine-making; Rømer chose 60 degrees to match the astronomical calculations of his time.",
      
          "Converting between them bridges the gap between terrestrial crafts and celestial observations."
        ],
        fundamentalEquality: {
          statement: "0°Ré = 7.5°Rø  and  80°Ré = 60°Rø",
          explanation: "Réaumur's 80-degree scale and Rømer's 60-degree scale meet at these key points, with Rømer's offset of 7.5°"
        },
        formula: {
          main: '°Rø = (°Ré × 5/4 × 21/40) + 7.5',
          alternate: '°Rø = °Ré × 0.65625 + 7.5'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius",
            explanation: "Multiply by 5/4 to expand Réaumur's 80-degree scale to Celsius's 100-degree scale"
          },
          {
            step: "Convert to Rømer",
            explanation: "Multiply by 21/40 and add 7.5 to reach Rømer's astronomical scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°Ré',                                     // fromUnit
          '°Rø = (°Ré × 5/4 × 21/40) + 7.5',          // formula
          `°Rø = (${valueToUse} × 5/4 × 21/40) + 7.5 = ${finalResult}°Rø`,  // calculation with actual values
          `${finalResult}°Rø`                                 // result
        ),
        realWorldExample: "A French wine-making manual from 1780 might specify fermentation at 16°Ré. Converting this to about 18°Rø helps us understand how Danish astronomers would have recorded the same temperature in their observational logs.",
        funFacts: [
          "Both scales were widely used in their respective regions during the 18th century, creating a fascinating mosaic of European temperature measurement.",
          "Réaumur's scale was preferred for industrial processes, while Rømer's was favored for scientific observations.",
          "The overlap between these scales shows how different European scientific traditions approached the same natural phenomena."
        ],
        inputValue: valueToUse
      };
    }

    // Réaumur to Newton
    if (fromUnit === 'Réaumur' && toUnit === 'Newton') {
      const step1 = parseFloat(valueToUse) * (5/4); // To Celsius
      const step2 = step1 * (33/100); // To Newton
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "When the precision of French wine-making meets the first scientific temperature scale, we bridge two fascinating chapters in the history of measurement. Réaumur's 80-degree scale, beloved by vintners and craftsmen, shares a common ancestor with Newton's pioneering 33-degree system - both born from careful observation of how materials respond to heat.",
      
          "While Réaumur watched alcohol expand in glass tubes, Newton had studied the behavior of linseed oil decades earlier. Their different approaches - R��aumur's industrial precision versus Newton's philosophical investigation - show how temperature measurement evolved to serve different needs.",
      
          "Converting between these scales connects the practical wisdom of French industry with the dawn of systematic temperature measurement."
        ],
        fundamentalEquality: {
          statement: "0°Ré = 0°N  and  80°Ré = 33°N",
          explanation: "Réaumur's industrial 80-degree scale maps to Newton's compact 33-degree range"
        },
        formula: {
          main: '°N = (°Ré × 5/4 × 33/100)',
          alternate: '°N = °Ré × 0.4125'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius",
            explanation: "Multiply by 5/4 to expand Réaumur's 80-degree scale to Celsius's 100-degree scale"
          },
          {
            step: "Convert to Newton",
            explanation: "Multiply by 33/100 to compress to Newton's 33-degree scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°Ré',                                     // fromUnit
          '°N = (°Ré × 5/4 × 33/100)',              // formula
          `°N = (${valueToUse} × 5/4 × 33/100) = ${finalResult}°N`,  // calculation with actual values
          `${finalResult}°N`                                 // result
        ),
        realWorldExample: "When a French brewing manual from 1780 specifies a fermentation temperature of 16°Ré, we can convert this to about 8.25°N to understand how Newton might have recorded the same temperature in his earlier experiments.",
        funFacts: [
          "Newton created his scale nearly 40 years before Réaumur, yet both men chose to base their measurements on the behavior of liquids.",
          "While Newton used linseed oil, Réaumur's choice of alcohol as a thermometric fluid proved more practical for everyday use.",
          "The conversion between these scales reveals how different approaches to standardization emerged in early scientific measurement."
        ],
        inputValue: valueToUse
      };
    }
    // Newton to Réaumur
    if (fromUnit === 'Newton' && toUnit === 'Réaumur') {
      const step1 = parseFloat(valueToUse) * (100/33); // To Celsius
      const step2 = step1 * (4/5); // To Réaumur
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "In the evolution of temperature measurement, Newton's candlelit experiments with linseed oil laid the groundwork for more refined scales to come. Decades later, Réaumur would create a more practical scale that would become the standard for French industry and wine-making.",
      
          "Converting from Newton's philosophical 33 degrees to Réaumur's practical 80 degrees traces the path from pure science to applied craft. Where Newton sought to understand the nature of heat itself, Réaumur aimed to give craftsmen a reliable tool for their trades.",
      
          "This conversion bridges the gap between theoretical investigation and practical application, between the solitary scientist and the bustling workshop."
        ],
        fundamentalEquality: {
          statement: "0°N = 0°Ré  and  33°N = 80°Ré",
          explanation: "Newton's compact 33-degree scale expands to match Réaumur's more detailed 80-degree range"
        },
        formula: {
          main: '°Ré = (°N × 100/33 × 4/5)',
          alternate: '°Ré = °N × 2.424242'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius",
            explanation: "Multiply by 100/33 to expand Newton's compressed scale to Celsius-sized degrees"
          },
          {
            step: "Convert to Réaumur",
            explanation: "Multiply by 4/5 to convert to Réaumur's 80-degree scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°N',                                     // fromUnit
          '°Ré = (°N × 100/33 × 4/5)',              // formula
          `°Ré = (${valueToUse} × 100/33 × 4/5) = ${finalResult}°Ré`,  // calculation with actual values
          `${finalResult}°Ré`                                 // result
        ),
        realWorldExample: "When Newton noted that metals began to glow at 25°N, French metalworkers using Réaumur's scale would have recorded this same temperature as about 60.6°Ré in their manufacturing guides.",
        funFacts: [
          "Newton's scale predated thermometers as we know them, while Réaumur's scale helped standardize their design.",
          "The conversion between these scales shows how temperature measurement evolved from theoretical to practical applications.",
          "Both scales influenced how later scientists thought about dividing the range between freezing and boiling points."
        ],
        inputValue: valueToUse
      };
    }

    // Réaumur to Delisle
    if (fromUnit === 'Réaumur' && toUnit === 'Delisle') {
      const step1 = parseFloat(valueToUse) * (5/4); // To Celsius
      const step2 = (100 - step1) * (3/2); // To Delisle
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "From the wine cellars of France to the frozen observatories of St. Petersburg, temperature measurement in the 1700s told two very different stories. Réaumur's scale helped French vintners perfect their craft, while Delisle's reverse scale helped Russian astronomers understand their harsh climate.",
      
          "These scales represent two opposing views of temperature: Réaumur's degrees rising with heat, perfect for monitoring fermentation, and Delisle's degrees increasing with cold, ideal for measuring Russia's winter extremes. One scale born in the temperate workshops of Paris, the other in the frigid observatories of St. Petersburg.",
      
          "Converting between them bridges not just different measurements, but different ways of thinking about heat and cold."
        ],
        fundamentalEquality: {
          statement: "0°Ré = 150°De  and  80°Ré = 0°De",
          explanation: "As Réaumur's scale rises with heat, Delisle's scale counts backward from boiling water"
        },
        formula: {
          main: '°De = (100 - (°Ré × 5/4)) × 3/2',
          alternate: '°De = (150 - °Ré × 15/8)'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius",
            explanation: "Multiply by 5/4 to expand Réaumur's 80-degree scale to Celsius's 100-degree scale"
          },
          {
            step: "Convert to Delisle",
            explanation: "Subtract from 100 and multiply by 3/2 to reach Delisle's reverse scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°Ré',                                     // fromUnit
          '°De = (100 - (°Ré × 5/4)) × 3/2',          // formula
          `°De = (100 - (${valueToUse} × 5/4)) × 3/2 = ${finalResult}°De`,  // calculation with actual values
          `${finalResult}°De`                                 // result
        ),
        realWorldExample: "When French wine-makers recorded optimal fermentation at 16°Ré, Russian scientists would have noted this same temperature as about 120°De in their astronomical logs - showing how different contexts led to different ways of measuring the same phenomenon.",
        funFacts: [
          "Réaumur and Delisle corresponded about their scales, representing a fascinating dialogue between Western European and Russian science.",
          "While Réaumur's scale was optimized for positive numbers in wine-making, Delisle's was designed for positive numbers in cold weather.",
          "The conversion between these scales reveals how different climates influenced the development of temperature measurement."
        ],
        inputValue: valueToUse
      };
    }

    // Delisle to Réaumur
    if (fromUnit === 'Delisle' && toUnit === 'Réaumur') {
      const step1 = parseFloat(valueToUse) * (2/3); // Convert to Celsius-sized steps
      const step2 = 100 - step1; // Invert to get Celsius
      const step3 = step2 * (4/5); // Convert to Réaumur
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "In the harsh winters of St. Petersburg, Joseph Delisle created a temperature scale that counted upward as mercury froze in his thermometers. Meanwhile, in the mild climate of Paris, Réaumur's scale helped craftsmen perfect their wines and brandies. These two scales, born of such different needs, tell us much about how science adapted to local conditions.",
      
          "Where Delisle needed to measure the bitter cold of Russian winters, Réaumur focused on the precise temperatures of fermentation. Their scales met in the middle, each serving their creators' unique purposes - one for the astronomer's observatory, the other for the vintner's cellar.",
      
          "Converting from Delisle to Réaumur bridges the gap between Russian astronomical science and French industrial craft."
        ],
        fundamentalEquality: {
          statement: "150°De = 0°Ré  and  0°De = 80°Ré",
          explanation: "Delisle's reverse-running scale meets Réaumur's conventional scale at these key points"
        },
        formula: {
          main: '°Ré = (100 - (°De × 2/3)) × 4/5',
          alternate: '°Ré = (150 - °De) × 8/15'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius-sized steps",
            explanation: "Multiply by 2/3 to convert from Delisle's 150-degree span to Celsius-sized intervals"
          },
          {
            step: "Invert the scale",
            explanation: "Subtract from 100 to flip Delisle's reverse numbering to match Celsius's direction"
          },
          {
            step: "Convert to Réaumur",
            explanation: "Multiply by 4/5 to compress to Réaumur's 80-degree scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°De',                                     // fromUnit
          '°Ré = (100 - (°De × 2/3)) × 4/5',          // formula
          `°Ré = (100 - (${valueToUse} × 2/3)) × 4/5 = ${finalResult}°Ré`,  // calculation with actual values
          `${finalResult}°Ré`                                 // result
        ),
        realWorldExample: "When Russian astronomers recorded a winter temperature of 180°De, French scientists could convert this to about -16°Ré, helping them understand the extreme conditions their colleagues worked in.",
        funFacts: [
          "The correspondence between Delisle and Réaumur shows how 18th-century scientists worked to standardize temperature measurement across Europe.",
          "While Delisle's scale dominated Russian science, Réaumur's scale became the standard for much of continental Europe.",
          "The need to convert between these scales helped drive the development of more universal temperature measurements."
        ],
        inputValue: valueToUse
      };
    }

    // Rømer to Newton
    if (fromUnit === 'Rømer' && toUnit === 'Newton') {
      const step1 = parseFloat(valueToUse) - 7.5; // Adjust zero point
      const step2 = step1 * (40/21); // To Celsius
      const step3 = step2 * (33/100); // To Newton
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "In the early days of scientific temperature measurement, two great minds approached the challenge from different angles. Ole Rømer, studying the stars from his Copenhagen observatory, created a scale that worked well with astronomical calculations. Meanwhile, in England, Isaac Newton had already pioneered systematic temperature measurement using linseed oil.",
      
          "Their scales, both created before the age of standardized thermometers, represent two different paths toward understanding heat. Rømer chose his zero point to avoid negative numbers in astronomical work, while Newton focused on creating equal divisions based on material behavior.",
      
          "Converting between these scales bridges two foundational moments in the history of temperature measurement."
        ],
        fundamentalEquality: {
          statement: "7.5°Rø = 0°N  and  60°Rø = 33°N",
          explanation: "Rømer's astronomical scale, with its 7.5° offset, maps to Newton's compact 33-degree range"
        },
        formula: {
          main: '°N = ((°Rø - 7.5) × 40/21 × 33/100)',
          alternate: '°N = (°Rø - 7.5) × 0.628571'
        },
        derivationSteps: [
          {
            step: "Adjust zero point",
            explanation: "Subtract 7.5 to align with Rømer's reference point"
          },
          {
            step: "Convert to Celsius",
            explanation: "Multiply by 40/21 to expand Rømer's compressed scale"
          },
          {
            step: "Convert to Newton",
            explanation: "Multiply by 33/100 to reach Newton's compact scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°Rø',                                     // fromUnit
          '°N = ((°Rø - 7.5) × 40/21 × 33/100)',    // formula
          `°N = (${valueToUse} - 7.5) × 40/21 × 33/100 = ${finalResult}°N`,  // calculation with actual values
          `${finalResult}°N`                                 // result
        ),
        realWorldExample: "When Rømer recorded 30°Rø for a chemical reaction in his observatory, this would have corresponded to about 14.5°N in Newton's earlier experiments - showing how different pioneers measured the same natural phenomena.",
        funFacts: [
          "Both Rømer and Newton created their scales before the widespread use of mercury thermometers.",
          "Their different approaches to zero points and scale divisions reflect the different scientific needs of astronomy and physics.",
          "Though they never met, their work laid parallel foundations for modern temperature measurement."
        ],
        inputValue: valueToUse
      };
    }

    // Newton to Rømer
    if (fromUnit === 'Newton' && toUnit === 'Rømer') {
      const step1 = parseFloat(valueToUse) * (100/33); // To Celsius
      const step2 = step1 * (21/40) + 7.5; // To Rømer
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "From Newton's quiet laboratory in Cambridge to Rømer's bustling observatory in Copenhagen, the early story of temperature measurement unfolded in parallel. Newton's careful observations of linseed oil's behavior gave us our first systematic temperature scale, while Rømer's astronomical needs led him to create a scale perfect for stargazing.",
      
          "Though separated by the North Sea and a generation, these two pioneers approached the challenge of measuring heat from different angles. Newton sought universal principles, while Rømer needed practical tools for astronomy. Their scales tell two stories of the same quest for scientific understanding.",
      
          "Converting from Newton to Rømer bridges the gap between experimental physics and practical astronomy in the dawn of modern science."
        ],
        fundamentalEquality: {
          statement: "0°N = 7.5°Rø  and  33°N = 60°Rø",
          explanation: "Newton's compact 33-degree scale maps to Rømer's astronomical range with its 7.5° offset"
        },
        formula: {
          main: '°Rø = (°N × 100/33 × 21/40) + 7.5',
          alternate: '°Rø = °N × 1.59091 + 7.5'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius",
            explanation: "Multiply by 100/33 to expand Newton's compressed scale to Celsius-sized degrees"
          },
          {
            step: "Convert to Rømer",
            explanation: "Multiply by 21/40 and add 7.5 to reach Rømer's astronomical scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°N',                                     // fromUnit
          '°Rø = (°N × 100/33 × 21/40) + 7.5',      // formula
          `°Rø = (${valueToUse} × 100/33 × 21/40) + 7.5 = ${finalResult}°Rø`,  // calculation with actual values
          `${finalResult}°Rø`                                 // result
        ),
        realWorldExample: "When Newton noted that metals began to glow at 25°N, astronomers using Rømer's scale would have recorded this same temperature as about 47.3°Rø in their observational logs.",
        funFacts: [
          "Newton and Rømer were contemporaries, though they never collaborated directly on temperature measurement.",
          "Both men were polymaths who approached temperature as part of broader scientific investigations.",
          "Their different choices of scale divisions reflected their different scientific purposes: Newton's physical experiments versus Rømer's astronomical observations."
        ],
        inputValue: valueToUse
      };
    }



    // Rømer to Delisle
    if (fromUnit === 'Rømer' && toUnit === 'Delisle') {
      const step1 = parseFloat(valueToUse) - 7.5; // Adjust zero point
      const step2 = step1 * (40/21); // To Celsius
      const step3 = (100 - step2) * (3/2); // To Delisle
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "From the astronomical observatories of Copenhagen to the frozen towers of St. Petersburg, two scientists developed temperature scales that would shape European astronomy. Rømer's scale, calibrated for Danish star-watching, met Delisle's reverse scale, designed for Russia's harsh winters.",
      
          "These scales tell the story of 18th-century astronomy from two perspectives: Rømer's practical approach to avoiding negative numbers in calculations, and Delisle's innovative reverse scale that made Siberian temperatures easier to record. Both men were astronomers first, thermometer makers second.",
      
          "Converting between their scales bridges the gap between Western and Eastern European astronomical traditions."
        ],
        fundamentalEquality: {
          statement: "7.5°Rø = 150°De  and  60°Rø = 0°De",
          explanation: "Rømer's astronomical scale with its 7.5° offset maps to Delisle's reverse-running scale"
        },
        formula: {
          main: '°De = (100 - ((°Rø - 7.5) × 40/21)) × 3/2',
          alternate: '°De = 150 - (°Rø - 7.5) × 2.857143'
        },
        derivationSteps: [
          {
            step: "Adjust zero point",
            explanation: "Subtract 7.5 to align with Rømer's reference point"
          },
          {
            step: "Convert to Celsius",
            explanation: "Multiply by 40/21 to expand Rømer's compressed scale"
          },
          {
            step: "Convert to Delisle",
            explanation: "Subtract from 100 and multiply by 3/2 to reach Delisle's reverse scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°Rø',                                     // fromUnit
          '°De = (100 - ((°Rø - 7.5) × 40/21)) × 3/2',  // formula
          `°De = (100 - (${valueToUse} - 7.5) × 40/21)) × 3/2 = ${finalResult}°De`,  // calculation with actual values
          `${finalResult}°De`                                 // result
        ),
        realWorldExample: "When Rømer recorded a mild spring evening at 15°Rø, Russian astronomers would have noted this same temperature as about 127°De - showing how different approaches to measurement could describe the same natural phenomena.",
        funFacts: [
          "Both Rømer and Delisle were primarily astronomers who created temperature scales to support their celestial observations.",
          "Their scales reflect the different challenges of astronomical observation in Denmark and Russia.",
          "The conversion between these scales was occasionally needed in early international astronomical collaborations."
        ],
        inputValue: valueToUse
      };
    }

    // Delisle to Rømer
    if (fromUnit === 'Delisle' && toUnit === 'Rømer') {
      const step1 = parseFloat(valueToUse) * (2/3); // Convert to Celsius-sized steps
      const step2 = 100 - step1; // Invert to get Celsius
      const step3 = step2 * (21/40) + 7.5; // Convert to Rømer
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "In the vast observatories of 18th-century Europe, two astronomers approached temperature measurement from opposite directions. Delisle, in the bitter cold of St. Petersburg, created a scale that counted upward as mercury froze. Rømer, in Copenhagen's milder climate, designed his scale to make astronomical calculations easier.",
      
          "Their scales reflect the unique challenges each faced: Delisle's reverse numbering perfect for Russian winters, Rømer's careful calibration ideal for celestial observations. Though they worked at opposite ends of Europe, both sought to bring precision to astronomical measurement.",
      
          "Converting from Delisle to Rømer traces the path of scientific knowledge across the Baltic Sea, from Russia's imperial observatories to Denmark's astronomical towers."
        ],
        fundamentalEquality: {
          statement: "150°De = 7.5°Rø  and  0°De = 60°Rø",
          explanation: "Delisle's reverse-running scale maps to Rømer's astronomical range with its 7.5° offset"
        },
        formula: {
          main: '°Rø = ((100 - (°De × 2/3)) × 21/40) + 7.5',
          alternate: '°Rø = (150 - °De) × 0.35 + 7.5'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius-sized steps",
            explanation: "Multiply by 2/3 to convert from Delisle's 150-degree span"
          },
          {
            step: "Invert the scale",
            explanation: "Subtract from 100 to flip Delisle's reverse numbering"
          },
          {
            step: "Convert to Rømer",
            explanation: "Multiply by 21/40 and add 7.5 to reach Rømer's scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°De',                                     // fromUnit
          '°Rø = ((100 - (°De × 2/3)) × 21/40) + 7.5',  // formula
          `°Rø = (100 - (${valueToUse} × 2/3)) × 21/40 + 7.5 = ${finalResult}°Rø`,  // calculation with actual values
          `${finalResult}°Rø`                                 // result
        ),
        realWorldExample: "When Russian astronomers recorded a winter temperature of 180°De, their Danish colleagues would have noted this as about 0°Rø, showing how these different scales could describe the same frigid conditions.",
        funFacts: [
          "Both scales were created primarily for astronomical work, though they approached the challenge very differently.",
          "The conversion between these scales was important for early international scientific collaboration.",
          "These scales represent two different solutions to the same problem: how to measure temperature precisely for astronomical observations."
        ],
        inputValue: valueToUse
      };
    }
    // Newton to Delisle
    if (fromUnit === 'Newton' && toUnit === 'Delisle') {
      const step1 = parseFloat(valueToUse) * (100/33); // To Celsius
      const step2 = (100 - step1) * (3/2); // To Delisle
      const finalResult = result || (step2.toFixed(2));

      return {
        introduction: [
          "From Newton's candlelit laboratory in Cambridge to Delisle's frost-covered observatory in St. Petersburg, the story of temperature measurement spans both distance and time. Newton's pioneering work with linseed oil in the 1700s provided the foundation that Delisle would build upon decades later in the harsh Russian winter.",
      
          "While Newton created his scale by observing how materials responded to heat, Delisle designed his reverse scale to make sense of extreme cold. Their different approaches - Newton's focus on heat's effects versus Delisle's practical need to measure freezing conditions - show how science adapts to different challenges.",
      
          "Converting between these scales bridges the gap between the dawn of systematic temperature measurement and its adaptation to extreme environments."
        ],
        fundamentalEquality: {
          statement: "0°N = 150°De  and  33°N = 0°De",
          explanation: "Newton's compact 33-degree scale maps to Delisle's reverse-running 150-degree range"
        },
        formula: {
          main: '°De = (100 - (°N × 100/33)) × 3/2',
          alternate: '°De = 150 - °N × 4.545454'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius",
            explanation: "Multiply by 100/33 to expand Newton's compressed scale to Celsius-sized degrees"
          },
          {
            step: "Convert to Delisle",
            explanation: "Subtract from 100 and multiply by 3/2 to reach Delisle's reverse scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°N',                                     // fromUnit
          '°De = (100 - (°N × 100/33)) × 3/2',      // formula
          `°De = (100 - (${valueToUse} × 100/33)) × 3/2 = ${finalResult}°De`,  // calculation with actual values
          `${finalResult}°De`                                 // result
        ),
        realWorldExample: "When Newton recorded that metals began to glow at 25°N, Russian astronomers would have noted this same temperature as about 36°De - showing how different approaches could describe the same phenomena.",
        funFacts: [
          "Newton created his scale nearly half a century before Delisle, yet both remain important in the history of temperature measurement.",
          "While Newton's scale counted up with increasing heat, Delisle's counted up with increasing cold - two opposite approaches to the same challenge.",
          "The conversion between these scales reveals how temperature measurement evolved to meet different scientific needs in different climates."
        ],
        inputValue: valueToUse
      };
    }

    // Delisle to Newton
    if (fromUnit === 'Delisle' && toUnit === 'Newton') {
      const step1 = parseFloat(valueToUse) * (2/3); // Convert to Celsius-sized steps
      const step2 = 100 - step1; // Invert to get Celsius
      const step3 = step2 * (33/100); // Convert to Newton
      const finalResult = result || (step3.toFixed(2));

      return {
        introduction: [
          "From the frozen expanses of imperial Russia to the scholarly confines of Cambridge University, the story of temperature measurement comes full circle. Delisle's reverse scale, designed for the bitter cold of St. Petersburg, meets Newton's pioneering system, the first truly scientific approach to measuring heat.",
      
          "These scales represent two different responses to the challenge of understanding temperature: Delisle's practical solution for measuring extreme cold, and Newton's philosophical investigation into the nature of heat itself. Their meeting point tells us much about how science adapts and evolves.",
      
          "Converting from Delisle to Newton bridges the gap between practical necessity and theoretical insight, between the astronomer's observatory and the natural philosopher's laboratory."
        ],
        fundamentalEquality: {
          statement: "150°De = 0°N  and  0°De = 33°N",
          explanation: "Delisle's reverse-running 150-degree scale maps to Newton's compact 33-degree range"
        },
        formula: {
          main: '°N = (100 - (°De  2/3)) × 33/100',
          alternate: '°N = (150 - °De) × 0.22'
        },
        derivationSteps: [
          {
            step: "Convert to Celsius-sized steps",
            explanation: "Multiply by 2/3 to convert from Delisle's 150-degree span"
          },
          {
            step: "Invert the scale",
            explanation: "Subtract from 100 to flip Delisle's reverse numbering"
          },
          {
            step: "Convert to Newton",
            explanation: "Multiply by 33/100 to reach Newton's compact scale"
          }
        ],
        conversionSteps: formatConversionSteps(
          valueToUse,                                // value
          '°De',                                     // fromUnit
          '°N = (100 - (°De  2/3)) × 33/100',      // formula
          `°N = (100 - (${valueToUse} × 2/3)) × 33/100 = ${finalResult}°N`,  // calculation with actual values
          `${finalResult}°N`                                 // result
        ),
        realWorldExample: "When Russian astronomers recorded a winter temperature of 180°De, we can now understand that Newton would have measured this as about 0°N - a fascinating connection between these two pioneering scientists' work.",
        funFacts: [
          "Though separated by decades, Newton and Delisle's scales both contributed fundamentally to our understanding of temperature measurement.",
          "The conversion between these scales shows how different scientific traditions approached the same natural phenomena.",
          "This conversion completes our historical temperature scale collection, connecting all the major temperature measurements of the 18th century."
        ],
        inputValue: valueToUse
      };
    }
      // Default case for unsupported conversions
      return {
        fundamentalEquality: {
          statement: "Conversion not supported",
          explanation: "This temperature conversion is not available"
        },
        formula: {
          main: 'No formula available',
          alternate: null
        },
        derivationSteps: [
          {
            step: "Not available",
            explanation: "This conversion is not supported"
          }
        ],
        conversionSteps: ['Conversion not available']
      };
    };

    const conversionData = getConversionData();

    return (
      <UnifiedSimpleConversionExplanation
        conversionType="temperature"
        fromUnit={fromUnit}
        toUnit={toUnit}
        inputValue={conversionData.inputValue}  // Use the value from conversionData
        result={result}
        darkMode={darkMode}
        {...conversionData}
      />
    );
  };

  export default TemperatureConversionExplanation;